import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { FormSelect, FormInput } from "@/components/Base/Form";
import { Menu } from "@/components/Base/Headless";
import Litepicker from "@/components/Base/Litepicker";
import TomSelect from "@/components/Base/TomSelect";
import dayjs from "dayjs";
import { getToken } from "@/utils/auth";
import Chart from "@/components/Base/Chart";
import type { ChartData } from "chart.js/auto";
import { Chart as ChartJS } from "chart.js/auto";
import Table from "@/components/Base/Table";

type SalesSummary = {
    totalHectoLitre: number;
    totalValue: number;
    totalQty: number;
};

type TeamTotal = { teamId: string; teamName: string; totalValue: number };

type OutletGroup = {
    id: string;
    groupName: string;
    companyId?: string;
    createdAt?: string;
    updatedAt?: string;
};

type ProductData = {
    productId: string;
    productName: string;
    opening: number | null;
    closing: number | null;
    qty: number | null;
    hectoLitre: number | null;
    price: number | null;
    total: number | null;
    grossWeightPerUOM: number | null;
};

type OutletSalesData = {
    outletId: string;
    outletName: string;
    products: Record<string, ProductData>; // key: productId
};

type SalesOutletGroupData = {
    products: Array<{ id: string; name: string; grossWeightPerUOM: number | null }>;
    outlets: OutletSalesData[];
};

type OutletGroupSalesData = {
    groupId: string;
    groupName: string;
    totalHectoLitre: number;
    totalSales: number;
    totalCartons: number;
};

type ProductSalesData = {
    productId: string;
    productName: string;
    totalHectoLitre: number;
    totalSales: number;
    totalCartons: number;
};

// Pricing types for Executive Summary
type PricingProduct = {
    id: string;
    name: string;
    productLine?: string | null;
    productCompetitor?: string[];
    displayOrder?: number | null;
};

type HeinekenPriceRow = {
    id: string;
    createdAt: string;
    productId: string | null;
    productName: string | null;
    productLine: string | null;
    price: number | null;
    outlet?: {
        id?: string;
        name?: string;
        outletGroup?: string | null;
        outletGroupRel?: {
            id: string;
            groupName: string;
        } | null;
    } | null;
};

type PricingTableRow = {
    ownProductId: string;
    ownProductName: string;
    ownPrice: number | null;
    competitorProductId: string | null;
    competitorProductName: string | null;
    competitorPrice: number | null;
};

type OutletGroupPriceStat = {
    outletGroupId: string;
    outletGroupName: string;
    price: number;
};

const formatNumber = (value: number, opts?: Intl.NumberFormatOptions) => {
    try {
        return new Intl.NumberFormat("en-MY", opts).format(value);
    } catch {
        return String(value);
    }
};

// Register plugin for bar labels
const barLabelPlugin = {
    id: 'barLabel',
    afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        
        chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            meta.data.forEach((bar: any, index: number) => {
                const value = dataset.data[index];
                if (value === null || value === undefined) return;
                
                const x = bar.x;
                const y = bar.y;
                
                // Determine label format based on dataset label
                let label = '';
                if (dataset.label === 'Hecto Litre') {
                    label = formatNumber(value, {
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                    }) + ' hL';
                } else if (dataset.label === 'Total Sales') {
                    label = `RM ${formatNumber(value, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`;
                } else {
                    label = String(value);
                }
                
                ctx.save();
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.font = '500 10px ui-sans-serif, system-ui';
                ctx.fillStyle = '#1e293b';
                
                // Always position labels outside the bar since we have 100px right padding
                // This ensures consistent behavior across all bars
                ctx.fillText(label, x + 6, y);
                
                ctx.restore();
            });
        });
    },
};

ChartJS.register(barLabelPlugin);

const buildTeamDonutData = (rows: TeamTotal[]): ChartData<"doughnut"> => {
    const labels = rows.map((r) => r.teamName || "Unknown Team");
    const values = rows.map((r) => Number(r.totalValue) || 0);
    const colors = [
        "#16A34A", // green
        "#2563EB", // blue
        "#F59E0B", // amber
        "#EF4444", // red
        "#A855F7", // purple
        "#14B8A6", // teal
        "#64748B", // slate
    ];

    return {
        labels,
        datasets: [
            {
                label: "Total Sales",
                data: values,
                backgroundColor: labels.map((_, i) => colors[i % colors.length]),
                borderColor: "rgba(255,255,255,0.9)",
                borderWidth: 2,
            },
        ],
    };
};

const buildTeamDonutOptions = () => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
            legend: {
                position: "bottom" as const,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 10,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const label = ctx.label || "Team";
                        const v = Number(ctx.parsed || 0);
                        return `${label}: RM ${formatNumber(v, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}`;
                    },
                },
            },
        },
    };
};

const buildTeamShareDonutData = (params: {
    teamName: string;
    teamValue: number;
    otherValue: number;
    teamColor: string;
}): ChartData<"doughnut"> => {
    const { teamName, teamValue, otherValue, teamColor } = params;
    return {
        labels: [teamName || "Team", "Others"],
        datasets: [
            {
                label: "Total Sales",
                data: [Number(teamValue) || 0, Number(otherValue) || 0],
                backgroundColor: [teamColor, "rgba(100, 116, 139, 0.25)"],
                borderColor: "rgba(255,255,255,0.9)",
                borderWidth: 2,
            },
        ],
    };
};

const buildTeamShareDonutOptions = () => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const label = ctx.label || "";
                        const v = Number(ctx.parsed || 0);
                        return `${label}: RM ${formatNumber(v, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}`;
                    },
                },
            },
        },
    };
};


type SalesData = OutletGroupSalesData | ProductSalesData;

const buildHectoLitreBarChartData = (data: SalesData[]): ChartData<"bar"> => {
    const labels = data.map((d) => {
        if ('groupName' in d) {
            return d.groupName || "Unknown Group";
        } else {
            return d.productName || "Unknown Product";
        }
    });
    return {
        labels,
        datasets: [
            {
                label: "Hecto Litre",
                data: data.map((d) => d.totalHectoLitre),
                backgroundColor: "rgba(0, 122, 61, 0.75)", // Heineken green
                borderColor: "#007A3D",
                borderWidth: 1,
                borderRadius: 4,
                barThickness: 8,
                maxBarThickness: 10,
                barPercentage: 1.0,
                categoryPercentage: 0.85,
            },
        ],
    };
};

const buildTotalSalesBarChartData = (data: SalesData[]): ChartData<"bar"> => {
    const labels = data.map((d) => {
        if ('groupName' in d) {
            return d.groupName || "Unknown Group";
        } else {
            return d.productName || "Unknown Product";
        }
    });
    return {
        labels,
        datasets: [
            {
                label: "Total Sales",
                data: data.map((d) => d.totalSales),
                backgroundColor: "rgba(37, 99, 235, 0.75)", // blue
                borderColor: "#2563EB",
                borderWidth: 1,
                borderRadius: 4,
                barThickness: 8,
                maxBarThickness: 10,
                barPercentage: 1.0,
                categoryPercentage: 0.85,
            },
        ],
    };
};

const buildHectoLitreBarChartOptions = (itemCount: number, data: SalesData[]) => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y" as const,
        layout: {
            padding: {
                top: 12,
                bottom: 12,
                right: 100, // Increased padding for labels to prevent cutoff
            },
        },
        plugins: {
            legend: {
                position: "top" as const,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const v = ctx.parsed?.x;
                        const label = ctx.dataset.label || "";
                        if (v === null || v === undefined) return `${label}: -`;
                        return `${label}: ${formatNumber(Number(v), {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                        })} hL`;
                    },
                },
            },
            barLabel: {
                afterDatasetsDraw: (chart: any) => {
                    const ctx = chart.ctx;
                    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                        const meta = chart.getDatasetMeta(datasetIndex);
                        meta.data.forEach((bar: any, index: number) => {
                            const value = dataset.data[index];
                            if (value === null || value === undefined) return;
                            
                            const x = bar.x;
                            const y = bar.y;
                            const label = formatNumber(value, {
                                minimumFractionDigits: 4,
                                maximumFractionDigits: 4,
                            }) + ' hL';
                            
                            ctx.save();
                            ctx.textAlign = 'left';
                            ctx.textBaseline = 'middle';
                            ctx.font = '500 10px ui-sans-serif, system-ui';
                            ctx.fillStyle = '#1e293b';
                            ctx.fillText(label, x + 4, y);
                            ctx.restore();
                        });
                    });
                },
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    font: { size: 9 },
                    padding: 2,
                    maxTicksLimit: 5,
                    callback: (value: any) => `${formatNumber(value, {
                        minimumFractionDigits: 4,
                        maximumFractionDigits: 4,
                    })}`,
                },
                grid: ({
                    color: "rgba(148, 163, 184, 0.35)",
                    borderDash: [2, 2],
                } as any),
                border: { display: false },
            },
            y: {
                offset: true,
                ticks: {
                    font: { size: 10 },
                    autoSkip: false,
                    padding: 6,
                },
                grid: { display: false },
                border: { display: false },
            },
        },
    };
};

const buildTotalSalesBarChartOptions = (itemCount: number, data: SalesData[]) => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y" as const,
        layout: {
            padding: {
                top: 12,
                bottom: 12,
                right: 100, // Increased padding for labels to prevent cutoff
            },
        },
        plugins: {
            legend: {
                position: "top" as const,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const v = ctx.parsed?.x;
                        const label = ctx.dataset.label || "";
                        if (v === null || v === undefined) return `${label}: -`;
                        return `${label}: RM ${Number(v).toFixed(2)}`;
                    },
                },
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    font: { size: 9 },
                    padding: 2,
                    maxTicksLimit: 5,
                    callback: (value: any) => `RM ${formatNumber(value, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}`,
                },
                grid: ({
                    color: "rgba(148, 163, 184, 0.35)",
                    borderDash: [2, 2],
                } as any),
                border: { display: false },
            },
            y: {
                offset: true,
                ticks: {
                    font: { size: 10 },
                    autoSkip: false,
                    padding: 6,
                },
                grid: { display: false },
                border: { display: false },
            },
        },
    };
};

function getBarChartHeightPx(itemCount: number) {
    // Horizontal bars: height should scale with number of categories
    // Reduced by 25% from current height (multiply by 0.75)
    return Math.max(300, itemCount * 40 + 120) * 0.75 * 0.75 * 0.75;
}

// Pricing helper functions for Executive Summary
function isOwnProductLine(productLine: string | null | undefined) {
    const v = (productLine || "").toLowerCase().trim();
    return v === "" || v.startsWith("own");
}

function isCompetitorProductLine(productLine: string | null | undefined) {
    const v = (productLine || "").toLowerCase().trim();
    return v.startsWith("competitor");
}

function pickModeOrMedian(prices: number[]): number | null {
    const normalized = prices
        .filter((p) => typeof p === "number" && Number.isFinite(p))
        .map((p) => Number(p.toFixed(2)));

    if (normalized.length === 0) return null;

    // Mode
    const counts = new Map<number, number>();
    for (const p of normalized) counts.set(p, (counts.get(p) || 0) + 1);

    let maxCount = 0;
    for (const c of counts.values()) maxCount = Math.max(maxCount, c);

    if (maxCount > 1) {
        const candidates: number[] = [];
        for (const [p, c] of counts.entries()) {
            if (c === maxCount) candidates.push(p);
        }
        return candidates.sort((a, b) => a - b)[0] ?? null;
    }

    // No mode → pick the median value
    const sorted = [...normalized].sort((a, b) => a - b);
    return sorted[Math.floor((sorted.length - 1) / 2)] ?? null;
}

function formatRM(value: number | null) {
    if (value === null || value === undefined) return "-";
    return value.toFixed(2);
}

function formatDiffRM(own: number | null, competitor: number | null) {
    if (own === null || competitor === null) return "-";
    const diff = own - competitor;
    return diff.toFixed(2);
}

function getPriceIndexPercentValue(own: number | null, competitor: number | null) {
    if (own === null || competitor === null || competitor === 0) return null;
    return ((own - competitor) / competitor) * 100;
}

function formatPriceIndexPercent(value: number | null) {
    if (value === null || value === undefined) return "-";
    return `${value.toFixed(1)}%`;
}

function buildChartLabels(rows: PricingTableRow[]) {
    return rows.map((r) => {
        const competitor = r.competitorProductName || "Competitor";
        return `${r.ownProductName} vs ${competitor}`;
    });
}

function buildPricingBarChartData(rows: PricingTableRow[]): ChartData<"bar"> {
    const labels = buildChartLabels(rows);
    return {
        labels,
        datasets: [
            {
                label: "Own",
                data: rows.map((r) => r.ownPrice),
                backgroundColor: "rgba(0, 122, 61, 0.75)", // Heineken green
                borderColor: "#007A3D",
                borderWidth: 1,
                borderRadius: 4,
                barPercentage: 0.95,
                categoryPercentage: 0.58,
                barThickness: 10,
                maxBarThickness: 12,
                order: 1,
            },
            {
                label: "Competitor",
                data: rows.map((r) => r.competitorPrice),
                backgroundColor: "rgba(220, 38, 38, 0.70)", // red
                borderColor: "#DC2626",
                borderWidth: 1,
                borderRadius: 4,
                barPercentage: 0.95,
                categoryPercentage: 0.58,
                barThickness: 10,
                maxBarThickness: 12,
                order: 2,
            },
        ],
    };
}

function buildPricingBarChartOptions(): any {
    return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        layout: {
            padding: {
                top: 12,
                bottom: 12,
            },
        },
        plugins: {
            legend: {
                position: "top",
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const v = ctx.parsed?.x;
                        if (v === null || v === undefined) return `${ctx.dataset.label}: -`;
                        return `${ctx.dataset.label}: RM ${Number(v).toFixed(2)}`;
                    },
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    font: { size: 9 },
                    padding: 2,
                    maxTicksLimit: 5,
                    callback: (value: any) => `${value}`,
                },
                grid: ({
                    color: "rgba(148, 163, 184, 0.35)",
                    borderDash: [2, 2],
                } as any),
                border: { display: false },
                stacked: false,
            },
            y: {
                offset: true,
                ticks: {
                    font: { size: 10 },
                    autoSkip: false,
                    padding: 6,
                    callback: function (value: any) {
                        const label = this.getLabelForValue(value);
                        if (typeof label !== "string") return label;

                        const wrap = (text: string, maxLen: number) => {
                            const words = text.split(/\s+/).filter(Boolean);
                            const lines: string[] = [];
                            let current = "";
                            for (const w of words) {
                                if (w.length > maxLen) {
                                    if (current) {
                                        lines.push(current);
                                        current = "";
                                    }
                                    for (let i = 0; i < w.length; i += maxLen) {
                                        lines.push(w.slice(i, i + maxLen));
                                    }
                                    continue;
                                }
                                const next = current ? `${current} ${w}` : w;
                                if (next.length > maxLen) {
                                    if (current) lines.push(current);
                                    current = w;
                                } else {
                                    current = next;
                                }
                            }
                            if (current) lines.push(current);
                            return lines;
                        };

                        const parts = label.split(" vs ");
                        const own = parts[0] || label;
                        const competitor = parts[1] ? `vs ${parts[1]}` : "";

                        const ownLines = wrap(own, 28);
                        const compLines = competitor ? wrap(competitor, 28) : [];
                        return [...ownLines, ...compLines];
                    },
                },
                grid: { display: false },
                border: { display: false },
                stacked: false,
            },
        },
    };
}

function getProductOutletGroupStats(params: {
    outletGroups: OutletGroup[];
    priceRows: HeinekenPriceRow[];
    productId: string;
}): {
    high: OutletGroupPriceStat | null;
    low: OutletGroupPriceStat | null;
} {
    const { outletGroups, priceRows, productId } = params;

    const pricesFor = (outletGroupId: string) => {
        return priceRows
            .filter(
                (r) =>
                    r.productId === productId &&
                    r.price !== null &&
                    r.outlet?.outletGroupRel?.id === outletGroupId
            )
            .map((r) => r.price as number);
    };

    const stats: OutletGroupPriceStat[] = [];
    outletGroups.forEach((g) => {
        const representative = pickModeOrMedian(pricesFor(g.id));
        if (representative === null) return;
        stats.push({
            outletGroupId: g.id,
            outletGroupName: g.groupName,
            price: representative,
        });
    });

    if (stats.length === 0) return { high: null, low: null };

    // Deterministic tie-break: first encountered in outletGroups order (already preserved in stats push order)
    let high = stats[0];
    let low = stats[0];
    for (const s of stats) {
        if (s.price > high.price) high = s;
        if (s.price < low.price) low = s;
    }

    return { high, low };
}

const makeCenterTextPlugin = (text: string) => {
    const safe = (text || "").trim();
    return {
        id: "centerText",
        afterDraw(chart: any) {
            if (!safe) return;
            const { ctx, chartArea } = chart;
            if (!ctx || !chartArea) return;

            const centerX = (chartArea.left + chartArea.right) / 2;
            const centerY = (chartArea.top + chartArea.bottom) / 2;

            // Try to derive inner radius from first arc element
            const arc = chart.getDatasetMeta?.(0)?.data?.[0];
            const innerRadius = arc?.innerRadius || Math.min(chartArea.width, chartArea.height) * 0.22;
            const maxWidth = innerRadius * 1.8;

            const wrap = (t: string) => {
                const words = t.split(/\s+/).filter(Boolean);
                const lines: string[] = [];
                let current = "";
                for (const w of words) {
                    const next = current ? `${current} ${w}` : w;
                    if (ctx.measureText(next).width <= maxWidth) {
                        current = next;
                    } else {
                        if (current) lines.push(current);
                        current = w;
                    }
                }
                if (current) lines.push(current);
                return lines.slice(0, 3); // keep it compact
            };

            ctx.save();
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = "#334155"; // slate-700

            const fontSize = Math.max(9, Math.min(13, Math.floor(innerRadius / 3)));
            ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial`;

            const lines = wrap(safe);
            const lineHeight = fontSize + 2;
            const startY = centerY - ((lines.length - 1) * lineHeight) / 2;

            lines.forEach((line, i) => {
                ctx.fillText(line, centerX, startY + i * lineHeight);
            });
            ctx.restore();
        },
    };
};

export default function Sales() {
    const { projectId } = useParams<{ projectId: string }>();
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get("companyId");
    
    // Storage key for persisting preferences (per project)
    const viewPrefsStorageKey = useMemo(() => {
        return projectId ? `sales_view_prefs_${projectId}` : null;
    }, [projectId]);

    // Initialize state with localStorage values if available
    const [salesView, setSalesView] = useState<"Executive Summary" | "Chain & Outlet" | "SKU" | "Data Report">(
        () => {
            if (!viewPrefsStorageKey) return "Executive Summary";
            try {
                const raw = localStorage.getItem(viewPrefsStorageKey);
                if (!raw) return "Executive Summary";
                const parsed = JSON.parse(raw);
                const sv = parsed?.salesView;
                if (sv === "Executive Summary" || sv === "Chain & Outlet" || sv === "SKU" || sv === "Data Report") {
                    return sv;
                }
            } catch {
                // ignore
            }
            return "Executive Summary";
        }
    );
    
    const [dateRange, setDateRange] = useState(() => {
        if (!viewPrefsStorageKey) {
            const d = dayjs().format("YYYY-MM-DD");
            return `${d} - ${d}`;
        }
        try {
            const raw = localStorage.getItem(viewPrefsStorageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                const dr = parsed?.dateRange;
                if (typeof dr === "string" && dr.trim()) {
                    return dr;
                }
            }
        } catch {
            // ignore
        }
        const d = dayjs().format("YYYY-MM-DD");
        return `${d} - ${d}`;
    });

    const apiUrl = useMemo(() => import.meta.env.VITE_API_URL || "http://localhost:4000", []);
    const [summary, setSummary] = useState<SalesSummary | null>(null);
    const [executiveSummaryTotalSales, setExecutiveSummaryTotalSales] = useState<number>(0);
    const [loadingExecutiveSummaryTotalSales, setLoadingExecutiveSummaryTotalSales] = useState(false);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [teamTotals, setTeamTotals] = useState<TeamTotal[]>([]);
    const [loadingTeamTotals, setLoadingTeamTotals] = useState(false);

    const teamTotalValueSum = useMemo(() => {
        return teamTotals.reduce((acc, r) => acc + (Number(r.totalValue) || 0), 0);
    }, [teamTotals]);

    // Outlet Groups for Data Report
    const [outletGroups, setOutletGroups] = useState<OutletGroup[]>([]);
    const [loadingOutletGroups, setLoadingOutletGroups] = useState(false);
    const [expandedOutletGroupIds, setExpandedOutletGroupIds] = useState<Set<string>>(
        () => new Set()
    );
    const [outletSalesData, setOutletSalesData] = useState<Map<string, SalesOutletGroupData>>(
        () => new Map()
    );
    const [loadingOutletSales, setLoadingOutletSales] = useState<Set<string>>(() => new Set());
    // Use ref to track which groups are currently being fetched (doesn't cause re-renders)
    const fetchingRef = useRef<Set<string>>(new Set());
    
    // Outlet Group Sales Data for Chain & Outlet view
    const [outletGroupSalesData, setOutletGroupSalesData] = useState<OutletGroupSalesData[]>([]);
    const [loadingOutletGroupSales, setLoadingOutletGroupSales] = useState(false);
    
    // Product Sales Data for SKU view
    const [productSalesData, setProductSalesData] = useState<ProductSalesData[]>([]);
    const [loadingProductSales, setLoadingProductSales] = useState(false);
    
    const [hectoLitreSortOrder, setHectoLitreSortOrder] = useState<"asc" | "desc">("desc");
    const [salesSortOrder, setSalesSortOrder] = useState<"asc" | "desc">("desc");
    
    // Table sorting state
    const [tableSortColumn, setTableSortColumn] = useState<"chain" | "sku" | "volume" | "sales" | "cartons" | null>(null);
    const [tableSortOrder, setTableSortOrder] = useState<"asc" | "desc">("asc");

    // Region filter (for Chain & Outlet view)
    const [regions, setRegions] = useState<Array<{ id: string; name: string }>>([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    
    // Chain (Outlet Group) filter - single selection only (for SKU view)
    const [selectedOutletGroup, setSelectedOutletGroup] = useState<string>("");
    
    // Outlet search for 2nd container (Chain & Outlet view)
    const [outlets, setOutlets] = useState<Array<{ id: string; name: string }>>([]);
    const [outletSearchQuery, setOutletSearchQuery] = useState<string>("");
    const [selectedOutlet, setSelectedOutlet] = useState<string>("");
    const [loadingOutlets, setLoadingOutlets] = useState(false);
    const [showOutletDropdown, setShowOutletDropdown] = useState(false);
    
    // Product search for 2nd container (SKU view)
    const [products, setProducts] = useState<Array<{ id: string; name: string }>>([]);
    const [productSearchQuery, setProductSearchQuery] = useState<string>("");
    const [selectedProduct, setSelectedProduct] = useState<string>("");
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const outletSearchRef = useRef<HTMLDivElement>(null);
    
    // Selected outlet sales data for 2nd container
    type SelectedOutletProductData = {
        productId: string;
        productName: string;
        hectoLitre: number;
        totalSales: number;
    };
    type SelectedOutletSalesData = {
        totalHectoLitre: number;
        totalSales: number;
        totalCartons: number;
        products: SelectedOutletProductData[];
    };
    const [selectedOutletSalesData, setSelectedOutletSalesData] = useState<SelectedOutletSalesData>({
        totalHectoLitre: 0,
        totalSales: 0,
        totalCartons: 0,
        products: [],
    });
    const [loadingSelectedOutletSales, setLoadingSelectedOutletSales] = useState(false);
    
    // Product sales by chain data (for SKU view 2nd container)
    type ProductChainSalesData = {
        groupId: string;
        groupName: string;
        hectoLitre: number;
        totalSales: number;
        totalCartons: number;
    };
    const [productChainSalesData, setProductChainSalesData] = useState<ProductChainSalesData[]>([]);
    const [loadingProductChainSales, setLoadingProductChainSales] = useState(false);
    
    // Pricing data for Executive Summary
    const [pricingProductsById, setPricingProductsById] = useState<Map<string, PricingProduct>>(() => new Map());
    const [pricingProductOrderIndexById, setPricingProductOrderIndexById] = useState<Map<string, number>>(() => new Map());
    const [loadingPricingProducts, setLoadingPricingProducts] = useState(false);
    const [pricingPriceRows, setPricingPriceRows] = useState<HeinekenPriceRow[]>([]);
    const [loadingPricingPrices, setLoadingPricingPrices] = useState(false);


    const dateRangeISO = useMemo(() => {
        const raw = (dateRange || "").trim();
        if (!raw) return null;
        const [startStrRaw, endStrRaw] = raw.split(" - ");
        const startStr = (startStrRaw || "").trim();
        const endStr = ((endStrRaw || "") || startStr).trim();
        if (!startStr) return null;

        const start = dayjs(startStr);
        const end = dayjs(endStr);
        if (!start.isValid() || !end.isValid()) return null;

        return {
            startISO: start.startOf("day").toISOString(),
            endISO: end.endOf("day").toISOString(),
        };
    }, [dateRange]);

    useEffect(() => {
        if (!projectId) return;
        if (!dateRangeISO) return;

        const controller = new AbortController();
        (async () => {
            try {
                setLoadingSummary(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", dateRangeISO.startISO);
                params.append("endDate", dateRangeISO.endISO);
                
                // Add region filters for Chain & Outlet view
                if (salesView === "Chain & Outlet" && selectedRegions.length > 0) {
                    selectedRegions.forEach((regionId) => {
                        params.append("regionIds", regionId);
                    });
                }
                
                console.log('[Sales] Fetching summary:', {
                    url: `${apiUrl}/sales/summary?${params.toString()}`,
                    projectId,
                    startDate: dateRangeISO.startISO,
                    endDate: dateRangeISO.endISO
                });

                const res = await fetch(`${apiUrl}/sales/summary?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                
                console.log('[Sales] Response status:', res.status, res.statusText);
                
                const json = await res.json();
                console.log('[Sales] Response JSON:', json);
                
                if (!res.ok || !json?.success) {
                    console.error('[Sales] API Error:', {
                        ok: res.ok,
                        status: res.status,
                        error: json?.error,
                        message: json?.message
                    });
                    throw new Error(json?.error || json?.message || "Failed to load sales summary");
                }
                const totals = json?.totals || {};
                console.log('[Sales] Parsed totals:', totals);
                setSummary({
                    totalHectoLitre: Number(totals.totalHectoLitre) || 0,
                    totalValue: Number(totals.totalValue) || 0,
                    totalQty: Number(totals.totalQty) || 0,
                });
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("[Sales] Failed to fetch sales summary:", {
                    error: err,
                    message: err?.message,
                    stack: err?.stack
                });
                setSummary(null);
            } finally {
                setLoadingSummary(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, dateRangeISO, projectId, selectedOutletGroup]);

    useEffect(() => {
        if (!projectId) return;
        if (!dateRangeISO) return;

        const controller = new AbortController();
        (async () => {
            try {
                setLoadingTeamTotals(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", dateRangeISO.startISO);
                params.append("endDate", dateRangeISO.endISO);
                
                // Add region filters for Chain & Outlet view
                if (salesView === "Chain & Outlet" && selectedRegions.length > 0) {
                    selectedRegions.forEach((regionId) => {
                        params.append("regionIds", regionId);
                    });
                }

                const res = await fetch(`${apiUrl}/sales/team-totals?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const json = await res.json();
                if (!res.ok || !json?.success) {
                    throw new Error(json?.error || json?.message || "Failed to load team totals");
                }
                const rows: any[] = Array.isArray(json?.teams) ? json.teams : [];
                setTeamTotals(
                    rows.map((r) => ({
                        teamId: String(r.teamId || ""),
                        teamName: String(r.teamName || "Unknown Team"),
                        totalValue: Number(r.totalValue) || 0,
                    }))
                );
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch team totals:", err);
                setTeamTotals([]);
            } finally {
                setLoadingTeamTotals(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, dateRangeISO, projectId, selectedOutletGroup]);

    // Load outlet group sales data for Chain & Outlet view from HeinekenPrice table
    useEffect(() => {
        if (salesView !== "Chain & Outlet") return;
        if (!projectId) return;
        if (!dateRangeISO) return;

        const controller = new AbortController();
        (async () => {
            try {
                setLoadingOutletGroupSales(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", dateRangeISO.startISO);
                params.append("endDate", dateRangeISO.endISO);
                params.append("page", "1");
                params.append("pageSize", "10000"); // Get all records for aggregation
                params.append("published", "true"); // Only fetch published records
                
                // Add region filters (this useEffect only runs for Chain & Outlet view)
                if (selectedRegions.length > 0) {
                    selectedRegions.forEach((regionId) => {
                        params.append("regionIds", regionId);
                    });
                }

                // Fetch HeinekenPrice data
                const res = await fetch(`${apiUrl}/heineken-prices?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });

                const json = await res.json();
                if (!res.ok) {
                    throw new Error(json?.error || json?.message || "Failed to load HeinekenPrice data");
                }

                const priceData: any[] = Array.isArray(json?.data) ? json.data : [];
                
                // Fetch outlet groups to get all groups
                const groupsRes = await fetch(`${apiUrl}/outlet-groups?page=1&pageSize=1000&sortBy=groupName&sortOrder=asc`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const groupsJson = await groupsRes.json();
                const allGroups: any[] = Array.isArray(groupsJson?.data) ? groupsJson.data : [];
                
                // Fetch products to get grossWeightPerUOM
                const productsRes = await fetch(`${apiUrl}/products?page=1&pageSize=1000${companyId ? `&companyId=${companyId}` : ""}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const productsJson = await productsRes.json();
                const allProducts: any[] = Array.isArray(productsJson?.data) ? productsJson.data : [];
                const productMap = new Map<string, { grossWeightPerUOM: number | null }>();
                allProducts.forEach((p: any) => {
                    productMap.set(String(p.id), {
                        grossWeightPerUOM: p.grossWeightPerUOM !== null && p.grossWeightPerUOM !== undefined 
                            ? Number(p.grossWeightPerUOM) 
                            : null,
                    });
                });
                
                // Aggregate data by outlet group
                const groupDataMap = new Map<string, {
                    groupId: string;
                    groupName: string;
                    totalHectoLitre: number;
                    totalSales: number;
                    totalCartons: number;
                }>();
                
                // Initialize all groups
                allGroups.forEach((g: any) => {
                    groupDataMap.set(String(g.id), {
                        groupId: String(g.id),
                        groupName: String(g.groupName || "Unknown Group"),
                        totalHectoLitre: 0,
                        totalSales: 0,
                        totalCartons: 0,
                    });
                });
                
                // Aggregate from price data
                priceData.forEach((item: any) => {
                    const groupId = item.outlet?.outletGroupRel?.id;
                    if (!groupId) return;
                    
                    const groupData = groupDataMap.get(String(groupId));
                    if (!groupData) return;
                    
                    // Skip competitor and premium products
                    if (item.productLine === 'Competitor' || item.productLine === 'Premium') {
                        return;
                    }
                    
                    const productId = item.productId;
                    const product = productId ? productMap.get(String(productId)) : null;
                    const grossWeightPerUOM = product?.grossWeightPerUOM ?? null;
                    
                    const qty = item.qty !== null && item.qty !== undefined ? Number(item.qty) : 0;
                    const total = item.total !== null && item.total !== undefined ? Number(item.total) : 0;
                    
                    // Skip if qty is 0 or null (no quantity means no actual sale, even if total exists)
                    // Products should only be included if they have actual quantity sold
                    if (qty === 0 || qty === null || qty === undefined) {
                        return;
                    }
                    
                    // Total cartons (qty)
                    groupData.totalCartons += qty;
                    
                    // Calculate hecto litre: qty * grossWeightPerUOM
                    if (grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                        groupData.totalHectoLitre += qty * grossWeightPerUOM;
                    }
                    
                    // Total sales (only add if total exists, but qty must be > 0 to reach here)
                    if (total > 0) {
                        groupData.totalSales += total;
                    }
                });
                
                // Build final groups data
                const groups: OutletGroupSalesData[] = Array.from(groupDataMap.values())
                    .filter(g => g.totalHectoLitre > 0 || g.totalSales > 0) // Only show groups with data
                    .map((g) => ({
                        groupId: g.groupId,
                        groupName: g.groupName,
                        totalHectoLitre: g.totalHectoLitre,
                        totalSales: g.totalSales,
                        totalCartons: g.totalCartons,
                    }))
                    .sort((a, b) => a.groupName.localeCompare(b.groupName));
                
                setOutletGroupSalesData(groups);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch outlet group sales data:", err);
                setOutletGroupSalesData([]);
            } finally {
                setLoadingOutletGroupSales(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, salesView, projectId, dateRangeISO, selectedOutletGroup, companyId]);

    // Calculate Total Sales for Executive Summary using same logic as Chain & Outlet
    useEffect(() => {
        if (salesView !== "Executive Summary") {
            setExecutiveSummaryTotalSales(0);
            return;
        }
        if (!projectId) return;
        if (!dateRangeISO) return;

        const controller = new AbortController();
        (async () => {
            try {
                setLoadingExecutiveSummaryTotalSales(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", dateRangeISO.startISO);
                params.append("endDate", dateRangeISO.endISO);
                params.append("page", "1");
                params.append("pageSize", "10000"); // Get all records for aggregation
                params.append("published", "true"); // Only fetch published records

                // Fetch HeinekenPrice data
                const res = await fetch(`${apiUrl}/heineken-prices?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });

                const json = await res.json();
                if (!res.ok) {
                    throw new Error(json?.error || json?.message || "Failed to load HeinekenPrice data");
                }

                const priceData: any[] = Array.isArray(json?.data) ? json.data : [];
                
                // Calculate total sales using same logic as Chain & Outlet
                let totalSales = 0;
                
                priceData.forEach((item: any) => {
                    // Skip competitor and premium products
                    if (item.productLine === 'Competitor' || item.productLine === 'Premium') {
                        return;
                    }
                    
                    const qty = item.qty !== null && item.qty !== undefined ? Number(item.qty) : 0;
                    const total = item.total !== null && item.total !== undefined ? Number(item.total) : 0;
                    
                    // Skip if qty is 0 or null (no quantity means no actual sale, even if total exists)
                    // Products should only be included if they have actual quantity sold
                    if (qty === 0 || qty === null || qty === undefined) {
                        return;
                    }
                    
                    // Total sales (only add if total exists, but qty must be > 0 to reach here)
                    if (total > 0) {
                        totalSales += total;
                    }
                });
                
                setExecutiveSummaryTotalSales(totalSales);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to calculate executive summary total sales:", err);
                setExecutiveSummaryTotalSales(0);
            } finally {
                setLoadingExecutiveSummaryTotalSales(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, salesView, projectId, dateRangeISO]);

    // Load product sales data for SKU view from HeinekenPrice table
    useEffect(() => {
        if (salesView !== "SKU") return;
        if (!projectId) return;
        if (!dateRangeISO) return;

        const controller = new AbortController();
        (async () => {
            try {
                setLoadingProductSales(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", dateRangeISO.startISO);
                params.append("endDate", dateRangeISO.endISO);
                params.append("page", "1");
                params.append("pageSize", "10000"); // Get all records for aggregation
                params.append("published", "true"); // Only fetch published records
                
                // Add outlet group filter (this useEffect only runs for SKU view)
                // Note: We filter client-side after fetching since backend doesn't support outletGroupId filter

                // Fetch HeinekenPrice data
                const res = await fetch(`${apiUrl}/heineken-prices?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });

                const json = await res.json();
                if (!res.ok) {
                    throw new Error(json?.error || json?.message || "Failed to load HeinekenPrice data");
                }

                let priceData: any[] = Array.isArray(json?.data) ? json.data : [];
                
                // Filter by selected outlet group
                if (selectedOutletGroup) {
                    priceData = priceData.filter((item: any) => {
                        const groupId = item.outlet?.outletGroupRel?.id;
                        return groupId && String(groupId) === selectedOutletGroup;
                    });
                }
                
                // Fetch products to get product names and grossWeightPerUOM
                const productsRes = await fetch(`${apiUrl}/products?page=1&pageSize=1000${companyId ? `&companyId=${companyId}` : ""}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const productsJson = await productsRes.json();
                const allProducts: any[] = Array.isArray(productsJson?.data) ? productsJson.data : [];
                const productMap = new Map<string, { name: string; grossWeightPerUOM: number | null }>();
                allProducts.forEach((p: any) => {
                    productMap.set(String(p.id), {
                        name: String(p.name || ""),
                        grossWeightPerUOM: p.grossWeightPerUOM !== null && p.grossWeightPerUOM !== undefined 
                            ? Number(p.grossWeightPerUOM) 
                            : null,
                    });
                });
                
                // Aggregate data by product
                const productDataMap = new Map<string, {
                    productId: string;
                    productName: string;
                    totalHectoLitre: number;
                    totalSales: number;
                    totalCartons: number;
                }>();
                
                // Aggregate from price data
                priceData.forEach((item: any) => {
                    const productId = item.productId;
                    if (!productId) return;
                    
                    // Skip competitor and premium products
                    if (item.productLine === 'Competitor' || item.productLine === 'Premium') {
                        return;
                    }
                    
                    const product = productMap.get(String(productId));
                    if (!product) return;
                    
                    const grossWeightPerUOM = product.grossWeightPerUOM ?? null;
                    const productName = product.name;
                    
                    const qty = item.qty !== null && item.qty !== undefined ? Number(item.qty) : 0;
                    const total = item.total !== null && item.total !== undefined ? Number(item.total) : 0;
                    
                    // Skip if qty is 0 or null (no quantity means no actual sale, even if total exists)
                    if (qty === 0 || qty === null || qty === undefined) {
                        return;
                    }
                    
                    // Initialize product data if not exists
                    if (!productDataMap.has(String(productId))) {
                        productDataMap.set(String(productId), {
                            productId: String(productId),
                            productName: productName,
                            totalHectoLitre: 0,
                            totalSales: 0,
                            totalCartons: 0,
                        });
                    }
                    
                    const productData = productDataMap.get(String(productId))!;
                    
                    // Total cartons (qty)
                    productData.totalCartons += qty;
                    
                    // Calculate hecto litre: qty * grossWeightPerUOM
                    if (grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                        productData.totalHectoLitre += qty * grossWeightPerUOM;
                    }
                    
                    // Total sales (only add if total exists, but qty must be > 0 to reach here)
                    if (total > 0) {
                        productData.totalSales += total;
                    }
                });
                
                // Build final products data
                const products: ProductSalesData[] = Array.from(productDataMap.values())
                    .filter(p => p.totalHectoLitre > 0 || p.totalSales > 0) // Only show products with data
                    .sort((a, b) => a.productName.localeCompare(b.productName));
                
                setProductSalesData(products);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch product sales data:", err);
                setProductSalesData([]);
            } finally {
                setLoadingProductSales(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, salesView, projectId, dateRangeISO, selectedOutletGroup, companyId]);

    // Load outlet groups for Data Report and filter dropdown (load for all views)
    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            try {
                setLoadingOutletGroups(true);
                const queryParams = new URLSearchParams();
                queryParams.append("page", "1");
                queryParams.append("pageSize", "100");
                queryParams.append("sortBy", "groupName");
                queryParams.append("sortOrder", "asc");

                const res = await fetch(
                    `${apiUrl}/outlet-groups?${queryParams.toString()}`,
                    {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    }
                );

                const json = await res.json();
                const firstPageGroups: OutletGroup[] = Array.isArray(json?.data)
                    ? json.data
                    : [];
                const totalPages = Number(json?.totalPages) || 1;

                // Fetch remaining pages if needed
                const allGroups = [...firstPageGroups];
                for (let page = 2; page <= totalPages; page++) {
                    const qp = new URLSearchParams(queryParams);
                    qp.set("page", String(page));
                    const resp = await fetch(
                        `${apiUrl}/outlet-groups?${qp.toString()}`,
                        {
                            signal: controller.signal,
                            headers: { Authorization: `Bearer ${getToken()}` },
                        }
                    );
                    const j = await resp.json();
                    const pageGroups: OutletGroup[] = Array.isArray(j?.data)
                        ? j.data
                        : [];
                    allGroups.push(...pageGroups);
                    if (page > 50) break;
                }

                setOutletGroups(allGroups);
                // Default: expand all outlet groups (only for Data Report view)
                if (salesView === "Data Report") {
                    setExpandedOutletGroupIds(new Set(allGroups.map((g) => g.id)));
                }
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch outlet groups:", err);
                setOutletGroups([]);
            } finally {
                setLoadingOutletGroups(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, salesView]);


    // Load Outlets for 2nd container search - fetch all pages
    useEffect(() => {
        const controller = new AbortController();
        (async () => {
            try {
                setLoadingOutlets(true);
                const allOutlets: Array<{ id: string; name: string }> = [];
                let currentPage = 1;
                let totalPages = 1;
                const pageSize = 100; // Use MAX_PAGE_SIZE

                // Fetch all pages
                do {
                    const queryParams = new URLSearchParams();
                    queryParams.append("page", String(currentPage));
                    queryParams.append("pageSize", String(pageSize));
                    queryParams.append("sortBy", "name");
                    queryParams.append("sortOrder", "asc");
                    // Don't filter by companyId to get all outlets that might have data
                    // The backend will apply company scope based on user role

                    const res = await fetch(`${apiUrl}/outlets?${queryParams.toString()}`, {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    });

                    const json = await res.json();
                    if (!res.ok) {
                        throw new Error(json?.error || json?.message || "Failed to fetch outlets");
                    }

                    const data = Array.isArray(json?.data) ? json.data : [];
                    allOutlets.push(
                        ...data.map((o: any) => ({
                            id: String(o.id || ""),
                            name: String(o.name || ""),
                        }))
                    );

                    totalPages = json?.totalPages || 1;
                    currentPage++;
                } while (currentPage <= totalPages && !controller.signal.aborted);

                setOutlets(allOutlets);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch outlets:", err);
                setOutlets([]);
            } finally {
                setLoadingOutlets(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, companyId]);

    // Load Products for 2nd container search (SKU view) - fetch all pages, filter by productLine = "Own"
    useEffect(() => {
        const controller = new AbortController();
        (async () => {
            try {
                setLoadingProducts(true);
                const allProducts: Array<{ id: string; name: string }> = [];
                let currentPage = 1;
                let totalPages = 1;
                const pageSize = 100; // Use MAX_PAGE_SIZE

                // Fetch all pages
                do {
                    const queryParams = new URLSearchParams();
                    queryParams.append("page", String(currentPage));
                    queryParams.append("pageSize", String(pageSize));
                    queryParams.append("productLine", "Own");
                    queryParams.append("sortBy", "name");
                    queryParams.append("sortOrder", "asc");
                    if (companyId) queryParams.append("companyId", companyId);

                    const res = await fetch(`${apiUrl}/products?${queryParams.toString()}`, {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    });

                    const json = await res.json();
                    if (!res.ok) {
                        throw new Error(json?.error || json?.message || "Failed to fetch products");
                    }

                    const data = Array.isArray(json?.data) ? json.data : [];
                    allProducts.push(
                        ...data.map((p: any) => ({
                            id: String(p.id || ""),
                            name: String(p.name || ""),
                        }))
                    );

                    totalPages = json?.totalPages || 1;
                    currentPage++;
                } while (currentPage <= totalPages && !controller.signal.aborted);

                setProducts(allProducts);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch products:", err);
                setProducts([]);
            } finally {
                setLoadingProducts(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, companyId]);

    // Close outlet/product dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (outletSearchRef.current && !outletSearchRef.current.contains(event.target as Node)) {
                setShowOutletDropdown(false);
                setShowProductDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Filter outlets based on search query (for Chain & Outlet view)
    const filteredOutlets = useMemo(() => {
        if (!outletSearchQuery.trim()) return [];
        const query = outletSearchQuery.toLowerCase();
        return outlets.filter((outlet) =>
            outlet.name.toLowerCase().includes(query)
        ).slice(0, 10); // Limit to 10 results
    }, [outlets, outletSearchQuery]);

    // Filter products based on search query (for SKU view)
    const filteredProducts = useMemo(() => {
        if (!productSearchQuery.trim()) return [];
        const query = productSearchQuery.toLowerCase();
        return products.filter((product) =>
            product.name.toLowerCase().includes(query)
        ).slice(0, 10); // Limit to 10 results
    }, [products, productSearchQuery]);

    // Get selected outlet name for title
    const selectedOutletName = useMemo(() => {
        if (!selectedOutlet) return null;
        const outlet = outlets.find((o) => o.id === selectedOutlet);
        return outlet?.name || null;
    }, [outlets, selectedOutlet]);

    // Get selected product name for title (SKU view)
    const selectedProductName = useMemo(() => {
        if (!selectedProduct) return null;
        const product = products.find((p) => p.id === selectedProduct);
        return product?.name || null;
    }, [products, selectedProduct]);

    // Fetch outlet sales data when outlet is selected
    useEffect(() => {
        if (!selectedOutlet || !dateRangeISO || salesView !== "Chain & Outlet") {
            setSelectedOutletSalesData({
                totalHectoLitre: 0,
                totalSales: 0,
                totalCartons: 0,
                products: [],
            });
            return;
        }

        const controller = new AbortController();
        (async () => {
            try {
                setLoadingSelectedOutletSales(true);
                const queryParams = new URLSearchParams();
                queryParams.append("page", "1");
                queryParams.append("pageSize", "10000");
                queryParams.append("outletId", selectedOutlet);
                queryParams.append("published", "true"); // Only fetch published records
                if (dateRangeISO.startISO) queryParams.append("startDate", dateRangeISO.startISO);
                if (dateRangeISO.endISO) queryParams.append("endDate", dateRangeISO.endISO);
                if (companyId) queryParams.append("companyId", companyId);

                const res = await fetch(`${apiUrl}/heineken-prices?${queryParams.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });

                const json = await res.json();
                if (!res.ok) {
                    throw new Error(json?.error || json?.message || "Failed to load outlet sales data");
                }

                let priceData: any[] = Array.isArray(json?.data) ? json.data : [];
                
                // Filter by selected outlet group if one is selected
                if (selectedOutletGroup) {
                    priceData = priceData.filter((item: any) => {
                        const groupId = item.outlet?.outletGroupRel?.id;
                        return groupId && String(groupId) === selectedOutletGroup;
                    });
                }

                // Fetch products to get grossWeightPerUOM
                const productsRes = await fetch(`${apiUrl}/products?page=1&pageSize=1000${companyId ? `&companyId=${companyId}` : ""}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const productsJson = await productsRes.json();
                const allProducts: any[] = Array.isArray(productsJson?.data) ? productsJson.data : [];
                const productMap = new Map<string, { name: string; grossWeightPerUOM: number | null }>();
                allProducts.forEach((p: any) => {
                    productMap.set(String(p.id), {
                        name: String(p.name || ""),
                        grossWeightPerUOM: p.grossWeightPerUOM !== null && p.grossWeightPerUOM !== undefined 
                            ? Number(p.grossWeightPerUOM) 
                            : null,
                    });
                });

                // Aggregate data by product for the selected outlet
                const productDataMap = new Map<string, {
                    productId: string;
                    productName: string;
                    hectoLitre: number;
                    totalSales: number;
                }>();

                let totalHectoLitre = 0;
                let totalSales = 0;
                let totalCartons = 0;

                priceData.forEach((item: any) => {
                    // Skip competitor and premium products
                    if (item.productLine === 'Competitor' || item.productLine === 'Premium') {
                        return;
                    }

                    const productId = item.productId;
                    if (!productId) return;

                    const product = productMap.get(String(productId));
                    if (!product) return;

                    const grossWeightPerUOM = product.grossWeightPerUOM ?? null;
                    const productName = product.name;

                    const qty = item.qty !== null && item.qty !== undefined ? Number(item.qty) : 0;
                    const total = item.total !== null && item.total !== undefined ? Number(item.total) : 0;

                    // Skip if qty is 0 or null (no quantity means no actual sale, even if total exists)
                    // Products should only be included if they have actual quantity sold
                    if (qty === 0 || qty === null || qty === undefined) {
                        return;
                    }

                    // Initialize product data if not exists
                    if (!productDataMap.has(String(productId))) {
                        productDataMap.set(String(productId), {
                            productId: String(productId),
                            productName: productName,
                            hectoLitre: 0,
                            totalSales: 0,
                        });
                    }

                    const productData = productDataMap.get(String(productId))!;

                    // Total cartons (qty)
                    totalCartons += qty;

                    // Calculate hecto litre: qty * grossWeightPerUOM
                    if (grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                        const hectoLitre = qty * grossWeightPerUOM;
                        productData.hectoLitre += hectoLitre;
                        totalHectoLitre += hectoLitre;
                    }

                    // Total sales (only add if total exists, but qty must be > 0 to reach here)
                    if (total > 0) {
                        productData.totalSales += total;
                        totalSales += total;
                    }
                });

                // Convert product map to array and sort by product name
                const products = Array.from(productDataMap.values())
                    .filter(p => p.hectoLitre > 0 || p.totalSales > 0)
                    .sort((a, b) => a.productName.localeCompare(b.productName));

                setSelectedOutletSalesData({
                    totalHectoLitre,
                    totalSales,
                    totalCartons,
                    products,
                });
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch outlet sales data:", err);
                setSelectedOutletSalesData({
                    totalHectoLitre: 0,
                    totalSales: 0,
                    totalCartons: 0,
                    products: [],
                });
            } finally {
                setLoadingSelectedOutletSales(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, companyId, selectedOutlet, dateRangeISO, selectedOutletGroup, salesView]);

    // Fetch product sales data by chain when product is selected (SKU view)
    useEffect(() => {
        if (!selectedProduct || !dateRangeISO || salesView !== "SKU") {
            setProductChainSalesData([]);
            return;
        }

        const controller = new AbortController();
        (async () => {
            try {
                setLoadingProductChainSales(true);
                const queryParams = new URLSearchParams();
                queryParams.append("page", "1");
                queryParams.append("pageSize", "10000");
                queryParams.append("productId", selectedProduct);
                queryParams.append("published", "true"); // Only fetch published records
                if (dateRangeISO.startISO) queryParams.append("startDate", dateRangeISO.startISO);
                if (dateRangeISO.endISO) queryParams.append("endDate", dateRangeISO.endISO);
                if (companyId) queryParams.append("companyId", companyId);

                const res = await fetch(`${apiUrl}/heineken-prices?${queryParams.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });

                const json = await res.json();
                if (!res.ok) {
                    throw new Error(json?.error || json?.message || "Failed to load product sales data");
                }

                let priceData: any[] = Array.isArray(json?.data) ? json.data : [];
                
                // Filter by selected outlet group if one is selected
                if (selectedOutletGroup) {
                    priceData = priceData.filter((item: any) => {
                        const groupId = item.outlet?.outletGroupRel?.id;
                        return groupId && String(groupId) === selectedOutletGroup;
                    });
                }

                // Fetch products to get grossWeightPerUOM
                const productsRes = await fetch(`${apiUrl}/products?page=1&pageSize=1000${companyId ? `&companyId=${companyId}` : ""}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const productsJson = await productsRes.json();
                const allProducts: any[] = Array.isArray(productsJson?.data) ? productsJson.data : [];
                const productMap = new Map<string, { grossWeightPerUOM: number | null }>();
                allProducts.forEach((p: any) => {
                    productMap.set(String(p.id), {
                        grossWeightPerUOM: p.grossWeightPerUOM !== null && p.grossWeightPerUOM !== undefined 
                            ? Number(p.grossWeightPerUOM) 
                            : null,
                    });
                });

                // Fetch outlet groups to get group names
                const groupsRes = await fetch(`${apiUrl}/outlet-groups?page=1&pageSize=1000&sortBy=groupName&sortOrder=asc`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const groupsJson = await groupsRes.json();
                const allGroups: any[] = Array.isArray(groupsJson?.data) ? groupsJson.data : [];
                const groupMap = new Map<string, string>();
                allGroups.forEach((g: any) => {
                    groupMap.set(String(g.id), String(g.groupName || ""));
                });

                // Aggregate data by outlet group (chain)
                const chainDataMap = new Map<string, {
                    groupId: string;
                    groupName: string;
                    hectoLitre: number;
                    totalSales: number;
                    totalCartons: number;
                }>();

                priceData.forEach((item: any) => {
                    // Skip competitor and premium products
                    if (item.productLine === 'Competitor' || item.productLine === 'Premium') {
                        return;
                    }

                    const outletGroup = item.outlet?.outletGroupRel;
                    if (!outletGroup || !outletGroup.id) return;

                    const groupId = String(outletGroup.id);
                    const groupName = groupMap.get(groupId) || outletGroup.groupName || "Unknown Chain";

                    const product = productMap.get(String(item.productId));
                    if (!product) return;

                    const grossWeightPerUOM = product.grossWeightPerUOM ?? null;

                    const qty = item.qty !== null && item.qty !== undefined ? Number(item.qty) : 0;
                    const total = item.total !== null && item.total !== undefined ? Number(item.total) : 0;

                    // Skip if qty is 0 or null (no quantity means no actual sale)
                    if (qty === 0 || qty === null || qty === undefined) {
                        return;
                    }

                    // Initialize chain data if not exists
                    if (!chainDataMap.has(groupId)) {
                        chainDataMap.set(groupId, {
                            groupId,
                            groupName,
                            hectoLitre: 0,
                            totalSales: 0,
                            totalCartons: 0,
                        });
                    }

                    const chainData = chainDataMap.get(groupId)!;

                    // Total cartons (qty)
                    chainData.totalCartons += qty;

                    // Calculate hecto litre: qty * grossWeightPerUOM
                    if (grossWeightPerUOM !== null && grossWeightPerUOM !== undefined) {
                        const hectoLitre = qty * grossWeightPerUOM;
                        chainData.hectoLitre += hectoLitre;
                    }

                    // Total sales
                    if (total > 0) {
                        chainData.totalSales += total;
                    }
                });

                // Convert chain map to array and sort by group name
                const chainData = Array.from(chainDataMap.values())
                    .filter(c => c.hectoLitre > 0 || c.totalSales > 0)
                    .sort((a, b) => a.groupName.localeCompare(b.groupName));

                setProductChainSalesData(chainData);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch product chain sales data:", err);
                setProductChainSalesData([]);
            } finally {
                setLoadingProductChainSales(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, companyId, selectedProduct, dateRangeISO, selectedOutletGroup, salesView]);

    // Build bar chart data for products by Hecto Litre
    const outletProductsHectoLitreChartData = useMemo(() => {
        const sortedProducts = [...selectedOutletSalesData.products].sort((a, b) => b.hectoLitre - a.hectoLitre);
        return {
            labels: sortedProducts.map((p) => p.productName || "Unknown Product"),
            datasets: [
                {
                    label: "Hecto Litre",
                    data: sortedProducts.map((p) => p.hectoLitre),
                    backgroundColor: "rgba(0, 122, 61, 0.75)", // Heineken green
                    borderColor: "#007A3D",
                    borderWidth: 1,
                    borderRadius: 4,
                    barThickness: 8,
                    maxBarThickness: 10,
                    barPercentage: 1.0,
                    categoryPercentage: 0.85,
                },
            ],
        };
    }, [selectedOutletSalesData.products]);

    // Build bar chart options for products by Hecto Litre
    const outletProductsHectoLitreChartOptions = useMemo(() => {
        const itemCount = selectedOutletSalesData.products.length;
        return {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y" as const,
            layout: {
                padding: {
                    top: 12,
                    bottom: 12,
                    right: 100,
                },
            },
            plugins: {
                legend: {
                    position: "top" as const,
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => {
                            const v = ctx.parsed?.x;
                            const label = ctx.dataset.label || "";
                            if (v === null || v === undefined) return `${label}: -`;
                            return `${label}: ${formatNumber(Number(v), {
                                minimumFractionDigits: 4,
                                maximumFractionDigits: 4,
                            })} hL`;
                        },
                    },
                },
                barLabel: {
                    afterDatasetsDraw: (chart: any) => {
                        const ctx = chart.ctx;
                        chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            meta.data.forEach((bar: any, index: number) => {
                                const value = dataset.data[index];
                                if (value === null || value === undefined) return;
                                
                                const x = bar.x;
                                const y = bar.y;
                                const label = formatNumber(value, {
                                    minimumFractionDigits: 4,
                                    maximumFractionDigits: 4,
                                }) + ' hL';
                                
                                ctx.save();
                                ctx.textAlign = 'left';
                                ctx.textBaseline = 'middle';
                                ctx.font = '500 10px ui-sans-serif, system-ui';
                                ctx.fillStyle = '#1e293b';
                                ctx.fillText(label, x + 4, y);
                                ctx.restore();
                            });
                        });
                    },
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        font: { size: 9 },
                        padding: 2,
                        maxTicksLimit: 5,
                        callback: (value: any) => `${formatNumber(value, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                        })}`,
                    },
                    grid: ({
                        color: "rgba(148, 163, 184, 0.35)",
                        borderDash: [2, 2],
                    } as any),
                    border: { display: false },
                },
                y: {
                    offset: true,
                    ticks: {
                        font: { size: 10 },
                        autoSkip: false,
                        padding: 6,
                    },
                    grid: { display: false },
                    border: { display: false },
                },
            },
        };
    }, [selectedOutletSalesData.products]);

    // Build bar chart data for products by Total Sales
    const outletProductsTotalSalesChartData = useMemo(() => {
        const sortedProducts = [...selectedOutletSalesData.products].sort((a, b) => b.totalSales - a.totalSales);
        return {
            labels: sortedProducts.map((p) => p.productName || "Unknown Product"),
            datasets: [
                {
                    label: "Total Sales",
                    data: sortedProducts.map((p) => p.totalSales),
                    backgroundColor: "rgba(37, 99, 235, 0.75)", // blue
                    borderColor: "#2563EB",
                    borderWidth: 1,
                    borderRadius: 4,
                    barThickness: 8,
                    maxBarThickness: 10,
                    barPercentage: 1.0,
                    categoryPercentage: 0.85,
                },
            ],
        };
    }, [selectedOutletSalesData.products]);

    // Build bar chart options for products by Total Sales
    const outletProductsTotalSalesChartOptions = useMemo(() => {
        const itemCount = selectedOutletSalesData.products.length;
        return {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y" as const,
            layout: {
                padding: {
                    top: 12,
                    bottom: 12,
                    right: 100,
                },
            },
            plugins: {
                legend: {
                    position: "top" as const,
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => {
                            const v = ctx.parsed?.x;
                            const label = ctx.dataset.label || "";
                            if (v === null || v === undefined) return `${label}: -`;
                            return `${label}: RM ${formatNumber(Number(v), {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}`;
                        },
                    },
                },
                barLabel: {
                    afterDatasetsDraw: (chart: any) => {
                        const ctx = chart.ctx;
                        chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                            const meta = chart.getDatasetMeta(datasetIndex);
                            meta.data.forEach((bar: any, index: number) => {
                                const value = dataset.data[index];
                                if (value === null || value === undefined) return;
                                
                                const x = bar.x;
                                const y = bar.y;
                                const label = 'RM ' + formatNumber(value, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                });
                                
                                ctx.save();
                                ctx.textAlign = 'left';
                                ctx.textBaseline = 'middle';
                                ctx.font = '500 10px ui-sans-serif, system-ui';
                                ctx.fillStyle = '#1e293b';
                                ctx.fillText(label, x + 4, y);
                                ctx.restore();
                            });
                        });
                    },
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        font: { size: 9 },
                        padding: 2,
                        maxTicksLimit: 5,
                        callback: (value: any) => `RM ${formatNumber(value, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}`,
                    },
                    grid: ({
                        color: "rgba(148, 163, 184, 0.35)",
                        borderDash: [2, 2],
                    } as any),
                    border: { display: false },
                },
                y: {
                    offset: true,
                    ticks: {
                        font: { size: 10 },
                        autoSkip: false,
                        padding: 6,
                    },
                    grid: { display: false },
                    border: { display: false },
                },
            },
        };
    }, [selectedOutletSalesData.products]);

    // Memoize sorted chart data to prevent unnecessary re-renders
    const sortedHectoLitreData = useMemo(() => {
        const data = salesView === "SKU" ? productSalesData : outletGroupSalesData;
        return [...data].sort((a, b) => {
            if (hectoLitreSortOrder === "asc") {
                return a.totalHectoLitre - b.totalHectoLitre;
            } else {
                return b.totalHectoLitre - a.totalHectoLitre;
            }
        });
    }, [salesView, outletGroupSalesData, productSalesData, hectoLitreSortOrder]);

    const sortedSalesData = useMemo(() => {
        const data = salesView === "SKU" ? productSalesData : outletGroupSalesData;
        return [...data].sort((a, b) => {
            if (salesSortOrder === "asc") {
                return a.totalSales - b.totalSales;
            } else {
                return b.totalSales - a.totalSales;
            }
        });
    }, [salesView, outletGroupSalesData, productSalesData, salesSortOrder]);

    // Memoize chart data and options to prevent unnecessary re-renders
    const hectoLitreChartData = useMemo(() => {
        return buildHectoLitreBarChartData(sortedHectoLitreData);
    }, [sortedHectoLitreData]);

    const hectoLitreChartOptions = useMemo(() => {
        return buildHectoLitreBarChartOptions(sortedHectoLitreData.length, sortedHectoLitreData);
    }, [sortedHectoLitreData]);

    const totalSalesChartData = useMemo(() => {
        return buildTotalSalesBarChartData(sortedSalesData);
    }, [sortedSalesData]);

    const totalSalesChartOptions = useMemo(() => {
        return buildTotalSalesBarChartOptions(sortedSalesData.length, sortedSalesData);
    }, [sortedSalesData]);

    // Persist view preferences to localStorage (per project)
    useEffect(() => {
        if (!viewPrefsStorageKey) return;
        try {
            localStorage.setItem(
                viewPrefsStorageKey,
                JSON.stringify({
                    v: 1,
                    salesView,
                    dateRange,
                })
            );
        } catch {
            // ignore localStorage errors
        }
    }, [viewPrefsStorageKey, salesView, dateRange]);

    const toggleOutletGroup = (groupId: string) => {
        setExpandedOutletGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    // Clear cached outlet sales data when date range changes
    useEffect(() => {
        if (salesView !== "Data Report") return;
        setOutletSalesData(new Map());
        fetchingRef.current.clear();
    }, [salesView, dateRangeISO]);

    // Fetch outlet sales data when outlet group is expanded
    useEffect(() => {
        if (salesView !== "Data Report") return;
        if (!projectId) return;
        if (!dateRangeISO) return;

        const controller = new AbortController();

        const fetchData = async (groupId: string) => {
            // Check if already loading or already loaded (using ref for loading check, state for data check)
            if (fetchingRef.current.has(groupId)) return; // Already fetching
            if (outletSalesData.has(groupId)) return; // Already loaded

            // Mark as fetching
            fetchingRef.current.add(groupId);
            setLoadingOutletSales((prev) => new Set(prev).add(groupId));

            try {
                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", dateRangeISO.startISO);
                params.append("endDate", dateRangeISO.endISO);

                const res = await fetch(
                    `${apiUrl}/sales/outlet-groups/${groupId}/outlets?${params.toString()}`,
                    {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    }
                );

                const json = await res.json();
                if (!res.ok || !json?.success) {
                    throw new Error(json?.error || json?.message || "Failed to load outlet data");
                }

                const products: Array<{ id: string; name: string; grossWeightPerUOM: number | null }> = 
                    Array.isArray(json?.products)
                        ? json.products.map((p: any) => ({
                              id: String(p.id || ""),
                              name: String(p.name || ""),
                              grossWeightPerUOM: p.grossWeightPerUOM !== null && p.grossWeightPerUOM !== undefined 
                                  ? Number(p.grossWeightPerUOM) 
                                  : null,
                          }))
                        : [];

                const outlets: OutletSalesData[] = Array.isArray(json?.outlets)
                    ? json.outlets.map((o: any) => {
                          const productsMap: Record<string, ProductData> = {};
                          if (o.products && typeof o.products === 'object') {
                              Object.keys(o.products).forEach((productId) => {
                                  const p = o.products[productId];
                                  productsMap[productId] = {
                                      productId: String(p.productId || productId),
                                      productName: String(p.productName || ""),
                                      opening: p.opening !== null && p.opening !== undefined ? Number(p.opening) : null,
                                      closing: p.closing !== null && p.closing !== undefined ? Number(p.closing) : null,
                                      qty: p.qty !== null && p.qty !== undefined ? Number(p.qty) : null,
                                      hectoLitre: p.hectoLitre !== null && p.hectoLitre !== undefined ? Number(p.hectoLitre) : null,
                                      price: p.price !== null && p.price !== undefined ? Number(p.price) : null,
                                      total: p.total !== null && p.total !== undefined ? Number(p.total) : null,
                                      grossWeightPerUOM: p.grossWeightPerUOM !== null && p.grossWeightPerUOM !== undefined 
                                          ? Number(p.grossWeightPerUOM) 
                                          : null,
                                  };
                              });
                          }
                          return {
                              outletId: String(o.outletId || ""),
                              outletName: String(o.outletName || ""),
                              products: productsMap,
                          };
                      })
                    : [];

                setOutletSalesData((prev) => {
                    const next = new Map(prev);
                    next.set(groupId, { products, outlets });
                    return next;
                });
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error(`Failed to fetch outlet data for group ${groupId}:`, err);
            } finally {
                // Remove from fetching ref and loading state
                fetchingRef.current.delete(groupId);
                setLoadingOutletSales((prev) => {
                    const next = new Set(prev);
                    next.delete(groupId);
                    return next;
                });
            }
        };

        // Fetch data for all expanded outlet groups
        expandedOutletGroupIds.forEach((groupId) => {
            fetchData(groupId);
        });

        return () => controller.abort();
    }, [apiUrl, salesView, projectId, dateRangeISO, expandedOutletGroupIds, selectedOutletGroup]);

    // Load pricing products for Executive Summary
    useEffect(() => {
        if (salesView !== "Executive Summary" || !projectId) return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingPricingProducts(true);
                const allProducts: PricingProduct[] = [];
                let page = 1;
                let totalPages = 1;

                while (page <= totalPages) {
                    const params = new URLSearchParams();
                    params.append("page", String(page));
                    params.append("pageSize", "100");
                    const res = await fetch(`${apiUrl}/products?${params.toString()}`, {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    });
                    const json = await res.json();
                    const pageData: any[] = Array.isArray(json?.data) ? json.data : [];
                    totalPages = Number(json?.totalPages) || 1;
                    page += 1;

                    pageData.forEach((p) => {
                        allProducts.push({
                            id: p.id,
                            name: p.name,
                            productLine: p.productLine ?? null,
                            productCompetitor: Array.isArray(p.productCompetitor)
                                ? p.productCompetitor
                                : [],
                            displayOrder: p.displayOrder !== null && p.displayOrder !== undefined ? Number(p.displayOrder) : null,
                        });
                    });

                    if (page > 50) break;
                }

                const map = new Map<string, PricingProduct>();
                allProducts.forEach((p) => map.set(p.id, p));
                setPricingProductsById(map);

                const orderMap = new Map<string, number>();
                allProducts.forEach((p, idx) => {
                    orderMap.set(p.id, idx);
                });
                setPricingProductOrderIndexById(orderMap);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch pricing products:", err);
                setPricingProductsById(new Map());
                setPricingProductOrderIndexById(new Map());
            } finally {
                setLoadingPricingProducts(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, projectId, salesView]);

    // Load pricing price rows for Executive Summary
    useEffect(() => {
        if (salesView !== "Executive Summary" || !projectId || !dateRangeISO) return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingPricingPrices(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);
                params.append("startDate", dateRangeISO.startISO);
                params.append("endDate", dateRangeISO.endISO);
                params.append("page", "1");
                params.append("pageSize", "10000");
                params.append("published", "true"); // Only fetch published records

                const res = await fetch(`${apiUrl}/heineken-prices?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const json = await res.json();
                const rows: any[] = Array.isArray(json?.data) ? json.data : [];

                setPricingPriceRows(
                    rows.map((r) => ({
                        id: r.id,
                        createdAt: r.createdAt,
                        productId: r.productId ?? null,
                        productName: r.productName ?? null,
                        productLine: r.productLine ?? null,
                        price: typeof r.price === "number" ? r.price : null,
                        outlet: r.outlet ?? null,
                    }))
                );
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                console.error("Failed to fetch pricing prices:", err);
                setPricingPriceRows([]);
            } finally {
                setLoadingPricingPrices(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, dateRangeISO, projectId, salesView]);

    // Compute pricing table by outlet group for Executive Summary
    const pricingTableByOutletGroupId = useMemo(() => {
        if (salesView !== "Executive Summary") return new Map<string, PricingTableRow[]>();
        
        const byGroup = new Map<string, PricingTableRow[]>();

        // Group price rows by outletGroupId
        const pricesByGroup = new Map<string, HeinekenPriceRow[]>();
        for (const row of pricingPriceRows) {
            const groupId = row.outlet?.outletGroupRel?.id;
            if (!groupId) continue;
            if (!pricesByGroup.has(groupId)) pricesByGroup.set(groupId, []);
            pricesByGroup.get(groupId)!.push(row);
        }

        for (const [groupId, rows] of pricesByGroup.entries()) {
            const ownRows = rows.filter(
                (r) => r.productId && isOwnProductLine(r.productLine) && r.price !== null
            );
            const competitorRows = rows.filter(
                (r) =>
                    r.productId &&
                    isCompetitorProductLine(r.productLine) &&
                    r.price !== null
            );

            // Map productId -> prices[]
            const ownPricesByProduct = new Map<string, number[]>();
            for (const r of ownRows) {
                if (!r.productId || r.price === null) continue;
                if (!ownPricesByProduct.has(r.productId))
                    ownPricesByProduct.set(r.productId, []);
                ownPricesByProduct.get(r.productId)!.push(r.price);
            }

            const competitorPricesByProduct = new Map<string, number[]>();
            for (const r of competitorRows) {
                if (!r.productId || r.price === null) continue;
                if (!competitorPricesByProduct.has(r.productId))
                    competitorPricesByProduct.set(r.productId, []);
                competitorPricesByProduct.get(r.productId)!.push(r.price);
            }

            const tableRows: PricingTableRow[] = [];

            for (const [ownProductId, prices] of ownPricesByProduct.entries()) {
                const ownProduct =
                    pricingProductsById.get(ownProductId) || (null as any);
                const ownProductName =
                    ownProduct?.name ||
                    ownRows.find((r) => r.productId === ownProductId)
                        ?.productName ||
                    "Unknown";

                const competitorIds: string[] = Array.isArray(
                    ownProduct?.productCompetitor
                )
                    ? ownProduct.productCompetitor
                    : [];
                const competitorProductId =
                    competitorIds.length > 0 ? competitorIds[0] : null;
                const competitorProduct =
                    competitorProductId ? pricingProductsById.get(competitorProductId) : null;
                const competitorProductName =
                    competitorProduct?.name ||
                    (competitorProductId
                        ? competitorRows.find(
                              (r) => r.productId === competitorProductId
                          )?.productName || null
                        : null);

                const ownPrice = pickModeOrMedian(prices);
                const competitorPrice =
                    competitorProductId && competitorPricesByProduct.has(competitorProductId)
                        ? pickModeOrMedian(
                              competitorPricesByProduct.get(competitorProductId) || []
                          )
                        : null;

                tableRows.push({
                    ownProductId,
                    ownProductName,
                    ownPrice,
                    competitorProductId,
                    competitorProductName,
                    competitorPrice,
                });
            }

            // Stable sort by product name
            tableRows.sort((a, b) => {
                const aIdx = pricingProductOrderIndexById.get(a.ownProductId);
                const bIdx = pricingProductOrderIndexById.get(b.ownProductId);
                if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
                if (aIdx !== undefined) return -1;
                if (bIdx !== undefined) return 1;
                return (a.ownProductName || "").localeCompare(b.ownProductName || "");
            });

            byGroup.set(groupId, tableRows);
        }

        return byGroup;
    }, [pricingPriceRows, pricingProductsById, pricingProductOrderIndexById, salesView]);

    // Calculate own product stats (highest/lowest prices by outlet group) per product for Executive Summary
    const executiveSummaryOwnProductStats = useMemo(() => {
        const emptyMap = new Map<string, { productId: string; productName: string; displayOrder: number | null; high: OutletGroupPriceStat | null; low: OutletGroupPriceStat | null }>();
        if (salesView !== "Executive Summary") return emptyMap;
        if (outletGroups.length === 0 || pricingPriceRows.length === 0) return emptyMap;

        // Get all own products with their IDs, names, and displayOrder
        const ownProductsMap = new Map<string, { name: string; displayOrder: number | null }>(); // productId -> { name, displayOrder }
        pricingPriceRows.forEach((r) => {
            if (r.productId && isOwnProductLine(r.productLine)) {
                if (!ownProductsMap.has(r.productId)) {
                    const product = pricingProductsById.get(r.productId);
                    ownProductsMap.set(r.productId, {
                        name: r.productName || product?.name || "Unknown",
                        displayOrder: product?.displayOrder ?? null,
                    });
                }
            }
        });

        if (ownProductsMap.size === 0) return emptyMap;

        const statsByProduct = new Map<string, { productId: string; productName: string; displayOrder: number | null; high: OutletGroupPriceStat | null; low: OutletGroupPriceStat | null }>();

        // Calculate stats for each own product
        ownProductsMap.forEach((productInfo, productId) => {
            const stats = getProductOutletGroupStats({
                outletGroups,
                priceRows: pricingPriceRows,
                productId,
            });

            statsByProduct.set(productId, {
                productId,
                productName: productInfo.name,
                displayOrder: productInfo.displayOrder,
                high: stats.high,
                low: stats.low,
            });
        });

        return statsByProduct;
    }, [salesView, outletGroups, pricingPriceRows, pricingProductsById]);

    // Note: keep projectId available for future data fetches, but we don't display a
    // title in the Executive Summary white container per UI request.

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div
                    style={{
                        width: "100vw",
                        maxWidth: "100vw",
                        marginLeft: "calc(-50vw + 50%)",
                        marginRight: "calc(-50vw + 50%)",
                        paddingLeft: "10rem",
                        paddingRight: "10rem",
                    }}
                >
                    <div style={{ width: "100%", maxWidth: "100%", overflow: "visible" }}>
                        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                            <div className="col-span-12">
                                {/* Filters (styled like Pricing/Stock) */}
                                <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center">
                                    <div className="relative inline-flex">
                                        <Lucide
                                            icon="Filter"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <FormSelect
                                            value={salesView}
                                            onChange={(e) =>
                                                setSalesView(
                                                    e.target.value as "Executive Summary" | "Chain & Outlet" | "SKU" | "Data Report"
                                                )
                                            }
                                            className="min-w-[160px] w-auto pl-9 pr-8 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm whitespace-nowrap"
                                        >
                                            <option value="Executive Summary">
                                                Sales: Executive Summary
                                            </option>
                                            <option value="Chain & Outlet">Sales: Chain & Outlet</option>
                                            <option value="SKU">Sales: SKU</option>
                                            <option value="Data Report">Sales: Data Report</option>
                                        </FormSelect>
                                    </div>

                                    <div className="relative inline-flex">
                                        <Lucide
                                            icon="Calendar"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <Litepicker
                                            value={dateRange}
                                            onChange={(e) => setDateRange((e.target?.value || "").trim())}
                                            options={{
                                                autoApply: true,
                                                singleMode: false,
                                                numberOfColumns: 2,
                                                numberOfMonths: 2,
                                                showWeekNumbers: true,
                                                dropdowns: {
                                                    minYear: 2020,
                                                    maxYear: null,
                                                    months: true,
                                                    years: true,
                                                },
                                                format: "YYYY-MM-DD",
                                            }}
                                            className="w-fit min-w-[220px] max-w-[320px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
                                        />
                                    </div>

                                    {/* Region Filter for Chain & Outlet view */}
                                    {salesView === "Chain & Outlet" && (
                                        <Menu className="relative" style={{ zIndex: 9999 }}>
                                            <Menu.Button className="relative inline-flex min-w-[100px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm whitespace-nowrap">
                                                <Lucide
                                                    icon="MapPin"
                                                    className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                                />
                                                Region
                                                {selectedRegions.length > 0 && (
                                                    <span className="ml-1 text-xs">({selectedRegions.length})</span>
                                                )}
                                            </Menu.Button>
                                            <Menu.Items className="w-80" style={{ zIndex: 9999 }}>
                                                <div className="p-4">
                                                    <div className="text-sm font-medium mb-4">Filter by Region</div>
                                                    
                                                    <div className="mb-4">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Region</div>
                                                            {regions.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                                                    onClick={() => {
                                                                        const allSelected = regions.every(r => selectedRegions.includes(r.id));
                                                                        if (allSelected) {
                                                                            setSelectedRegions([]);
                                                                        } else {
                                                                            setSelectedRegions(regions.map(r => r.id));
                                                                        }
                                                                    }}
                                                                >
                                                                    {regions.every(r => selectedRegions.includes(r.id)) ? 'Deselect all' : 'Select all'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                                            {regions.length === 0 ? (
                                                                <div className="text-xs text-slate-400">No regions available</div>
                                                            ) : (
                                                                regions.map((region) => (
                                                                    <label key={region.id} className="flex items-center cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input mr-2"
                                                                            checked={selectedRegions.includes(region.id)}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setSelectedRegions([...selectedRegions, region.id]);
                                                                                } else {
                                                                                    setSelectedRegions(selectedRegions.filter(id => id !== region.id));
                                                                                }
                                                                            }}
                                                                        />
                                                                        <span className="text-xs">{region.name}</span>
                                                                    </label>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Clear Filter Button */}
                                                    {selectedRegions.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="w-full mt-2 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-800 hover:bg-slate-300 dark:hover:bg-darkmode-700 rounded"
                                                            onClick={() => {
                                                                setSelectedRegions([]);
                                                            }}
                                                        >
                                                            Clear Filter
                                                        </button>
                                                    )}
                                                </div>
                                            </Menu.Items>
                                        </Menu>
                                    )}

                                    {/* Chain (Outlet Group) Filter for SKU view - Single Selection */}
                                    {salesView === "SKU" && (
                                        <div className="relative inline-flex">
                                            <Lucide
                                                icon="Building2"
                                                className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                            />
                                            <FormSelect
                                                value={selectedOutletGroup}
                                                onChange={(e) => setSelectedOutletGroup(e.target.value)}
                                                className="min-w-[160px] w-auto pl-9 pr-8 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm whitespace-nowrap"
                                            >
                                                <option value="">All Chains</option>
                                                {outletGroups.map((group) => (
                                                    <option key={group.id} value={group.id}>
                                                        {group.groupName}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                        </div>
                                    )}

                                    {/* Active Filter Display */}
                                    {salesView === "Chain & Outlet" && selectedRegions.length > 0 && (
                                        <div className="relative inline-flex items-center px-3 py-2 rounded-[0.5rem] bg-slate-50 dark:bg-darkmode-800 border border-slate-200 dark:border-slate-700">
                                            <div className="flex items-center gap-2">
                                                <Lucide icon="Filter" className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Active Filters:</span>
                                                <div className="flex flex-wrap gap-1.5">
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                                                        <Lucide icon="MapPin" className="w-3 h-3" />
                                                        <span className="font-medium">Regions:</span>
                                                        <span>
                                                            {selectedRegions.length === regions.length
                                                                ? "All"
                                                                : selectedRegions
                                                                      .map((id) => regions.find((r) => r.id === id)?.name || id)
                                                                      .join(", ")}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {salesView === "SKU" && selectedOutletGroup && (
                                        <div className="relative inline-flex items-center px-3 py-2 rounded-[0.5rem] bg-slate-50 dark:bg-darkmode-800 border border-slate-200 dark:border-slate-700">
                                            <div className="flex items-center gap-2">
                                                <Lucide icon="Filter" className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Active Filter:</span>
                                                <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                                                    <Lucide icon="Building2" className="w-3 h-3" />
                                                    <span className="font-medium">Chain:</span>
                                                    <span>
                                                        {outletGroups.find((g) => g.id === selectedOutletGroup)?.groupName || selectedOutletGroup}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Content (placeholder) */}
                                <div className="mt-6 grid grid-cols-12 gap-x-6 gap-y-6">
                                    {salesView === "Executive Summary" ? (
                                        <>
                                            {/* 1) Executive Summary cards */}
                                            <div className="col-span-12">
                                                <div className="p-[0.9375rem] box">
                                                    <div className="grid grid-cols-12 gap-3">
                                                        <div className="col-span-12 md:col-span-4">
                                                            <div className="p-[0.9375rem] box">
                                                                <div className="flex items-center gap-2">
                                                                    <Lucide
                                                                        icon="Droplet"
                                                                        className="w-3.5 h-3.5"
                                                                    />
                                                                    <div className="text-xs font-medium">
                                                                        Total Volume
                                                                    </div>
                                                                </div>
                                                                <div className="mt-1.5 text-xl font-medium tabular-nums">
                                                                    {loadingSummary
                                                                        ? "..."
                                                                        : `${formatNumber(
                                                                              summary?.totalHectoLitre || 0,
                                                                              {
                                                                                  minimumFractionDigits: 4,
                                                                                  maximumFractionDigits: 4,
                                                                              }
                                                                          )} hL`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-span-12 md:col-span-4">
                                                            <div className="p-[0.9375rem] box">
                                                                <div className="flex items-center gap-2">
                                                                    <Lucide
                                                                        icon="CircleDollarSign"
                                                                        className="w-3.5 h-3.5"
                                                                    />
                                                                    <div className="text-xs font-medium">
                                                                        Total Value
                                                                    </div>
                                                                </div>
                                                                <div className="mt-1.5 text-xl font-medium tabular-nums">
                                                                    {loadingSummary || loadingExecutiveSummaryTotalSales
                                                                        ? "..."
                                                                        : `RM ${formatNumber(
                                                                              executiveSummaryTotalSales,
                                                                              {
                                                                                  minimumFractionDigits: 2,
                                                                                  maximumFractionDigits: 2,
                                                                              }
                                                                          )}`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-span-12 md:col-span-4">
                                                            <div className="p-[0.9375rem] box">
                                                                <div className="flex items-center gap-2">
                                                                    <Lucide
                                                                        icon="Boxes"
                                                                        className="w-3.5 h-3.5"
                                                                    />
                                                                    <div className="text-xs font-medium">
                                                                        Total Quantity (in cartons)
                                                                    </div>
                                                                </div>
                                                                <div className="mt-1.5 text-xl font-medium tabular-nums">
                                                                    {loadingSummary
                                                                        ? "..."
                                                                        : formatNumber(
                                                                              summary?.totalQty || 0,
                                                                              {
                                                                                  maximumFractionDigits: 2,
                                                                              }
                                                                          )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* 2nd White Container - Price By: Chain */}
                                            <div className="col-span-12 mt-[0.125rem]">
                                                <div className="grid grid-cols-12 gap-x-6 gap-y-6">
                                                    {loadingOutletGroups ? (
                                                        <div className="col-span-12 flex items-center justify-center py-10">
                                                            <div className="text-center">
                                                                <Lucide
                                                                    icon="Loader2"
                                                                    className="w-7 h-7 animate-spin mx-auto mb-2"
                                                                />
                                                                <div>Loading outlet groups...</div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                            <>
                                                                {/* Outlet Groups - 70% width */}
                                                                <div className="col-span-12 lg:col-span-9">
                                                                    {outletGroups.map((g) => (
                                                                        <div key={g.id} className="mb-[0.3rem]">
                                                                            <div className="p-5 box">
                                                                                <div className="flex items-center gap-3">
                                                                                    <div className="text-base font-medium">
                                                                                        {g.groupName}
                                                                                    </div>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="ml-auto inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                                                                        onClick={() => toggleOutletGroup(g.id)}
                                                                                        aria-expanded={expandedOutletGroupIds.has(
                                                                                            g.id
                                                                                        )}
                                                                                        aria-controls={`outletgroup-${g.id}`}
                                                                                    >
                                                                                        <Lucide
                                                                                            icon={
                                                                                                expandedOutletGroupIds.has(
                                                                                                    g.id
                                                                                                )
                                                                                                    ? "ChevronUp"
                                                                                                    : "ChevronDown"
                                                                                            }
                                                                                            className="w-5 h-5 stroke-[1.3]"
                                                                                        />
                                                                                    </button>
                                                                                </div>

                                                                                {/* Table */}
                                                                                {expandedOutletGroupIds.has(g.id) && (
                                                                                    <div
                                                                                        id={`outletgroup-${g.id}`}
                                                                                        className="mt-4"
                                                                                    >
                                                                                        <div className="overflow-auto">
                                                                                            <Table className="border-b border-slate-200/60 text-xs">
                                                                                        <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                                                                                            <Table.Tr>
                                                                                                <Table.Td className="py-2 font-medium bg-slate-50/70 dark:bg-darkmode-700/30">
                                                                                                    Product
                                                                                                </Table.Td>
                                                                                                <Table.Td className="py-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30">
                                                                                                    RM
                                                                                                </Table.Td>
                                                                                                <Table.Td className="py-2 font-medium text-center bg-yellow-50/70 dark:bg-darkmode-700/30">
                                                                                                    Diff (RM)
                                                                                                </Table.Td>
                                                                                                <Table.Td className="py-2 font-medium text-center bg-yellow-50/70 dark:bg-darkmode-700/30 leading-tight">
                                                                                                    <div>Price</div>
                                                                                                    <div>Index (%)</div>
                                                                                                </Table.Td>
                                                                                                <Table.Td className="py-2 font-medium text-center bg-danger/5 dark:bg-darkmode-700/30">
                                                                                                    RM
                                                                                                </Table.Td>
                                                                                                <Table.Td className="py-2 font-medium bg-danger/5 dark:bg-darkmode-700/30">
                                                                                                    Competitor
                                                                                                </Table.Td>
                                                                                            </Table.Tr>
                                                                                        </Table.Thead>
                                                                                        <Table.Tbody>
                                                                                            {!dateRangeISO ? (
                                                                                                <Table.Tr>
                                                                                                    <Table.Td
                                                                                                        className="py-4 text-slate-500"
                                                                                                        colSpan={6}
                                                                                                    >
                                                                                                        Please select a date range.
                                                                                                    </Table.Td>
                                                                                                </Table.Tr>
                                                                                            ) : loadingPricingPrices || loadingPricingProducts ? (
                                                                                                <Table.Tr>
                                                                                                    <Table.Td
                                                                                                        className="py-4 text-slate-500"
                                                                                                        colSpan={6}
                                                                                                    >
                                                                                                        Loading pricing data...
                                                                                                    </Table.Td>
                                                                                                </Table.Tr>
                                                                                            ) : (pricingTableByOutletGroupId.get(g.id) || [])
                                                                                                  .length === 0 ? (
                                                                                                <Table.Tr>
                                                                                                    <Table.Td
                                                                                                        className="py-4 text-slate-500"
                                                                                                        colSpan={6}
                                                                                                    >
                                                                                                        No pricing data for this date range.
                                                                                                    </Table.Td>
                                                                                                </Table.Tr>
                                                                                            ) : (
                                                                                                (pricingTableByOutletGroupId.get(
                                                                                                    g.id
                                                                                                ) || []).map((row) => (
                                                                                                    <Table.Tr
                                                                                                        key={`${g.id}_${row.ownProductId}`}
                                                                                                        className="[&_td]:border-b [&_td]:border-slate-200/60"
                                                                                                    >
                                                                                                        <Table.Td className="py-2 bg-slate-50/40 dark:bg-darkmode-700/20">
                                                                                                            {row.ownProductName}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td className="py-2 text-right tabular-nums bg-slate-50/40 dark:bg-darkmode-700/20">
                                                                                                            {formatRM(row.ownPrice)}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td className="py-2 text-right tabular-nums bg-yellow-50/40 dark:bg-darkmode-700/20">
                                                                                                            {formatDiffRM(
                                                                                                                row.ownPrice,
                                                                                                                row.competitorPrice
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td className="py-2 text-right tabular-nums bg-yellow-50/40 dark:bg-darkmode-700/20">
                                                                                                            {(() => {
                                                                                                                const priceIndex =
                                                                                                                    getPriceIndexPercentValue(
                                                                                                                        row.ownPrice,
                                                                                                                        row.competitorPrice
                                                                                                                    );
                                                                                                                const isNegative =
                                                                                                                    priceIndex !== null &&
                                                                                                                    priceIndex < 0;
                                                                                                                return (
                                                                                                                    <span
                                                                                                                        className={
                                                                                                                            isNegative
                                                                                                                                ? "text-danger"
                                                                                                                                : ""
                                                                                                                        }
                                                                                                                    >
                                                                                                                        {formatPriceIndexPercent(
                                                                                                                            priceIndex
                                                                                                                        )}
                                                                                                                    </span>
                                                                                                                );
                                                                                                            })()}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td className="py-2 text-right tabular-nums bg-danger/5 dark:bg-darkmode-700/20">
                                                                                                            {formatRM(
                                                                                                                row.competitorPrice
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td className="py-2 bg-danger/5 dark:bg-darkmode-700/20">
                                                                                                            {row.competitorProductName ||
                                                                                                                "-"}
                                                                                                        </Table.Td>
                                                                                                    </Table.Tr>
                                                                                                ))
                                                                                            )}
                                                                                        </Table.Tbody>
                                                                                    </Table>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Own Product Boxes - 30% width */}
                                                        <div className="col-span-12 lg:col-span-3">
                                                            <div className="flex flex-col gap-[0.3rem]">
                                                                {Array.from(executiveSummaryOwnProductStats.values())
                                                                    .sort((a, b) => {
                                                                        // Follow the same sorting logic as Products/index.tsx
                                                                        // Products with displayOrder come first, sorted by displayOrder ascending
                                                                        // Products without displayOrder come after, sorted by name
                                                                        const aHasOrder = a.displayOrder !== null && a.displayOrder !== undefined;
                                                                        const bHasOrder = b.displayOrder !== null && b.displayOrder !== undefined;

                                                                        if (aHasOrder && bHasOrder) {
                                                                            // Both have displayOrder - sort by displayOrder
                                                                            return a.displayOrder! - b.displayOrder!;
                                                                        } else if (aHasOrder && !bHasOrder) {
                                                                            // a has order, b doesn't - a comes first
                                                                            return -1;
                                                                        } else if (!aHasOrder && bHasOrder) {
                                                                            // b has order, a doesn't - b comes first
                                                                            return 1;
                                                                        } else {
                                                                            // Neither has displayOrder - sort by name
                                                                            return a.productName.localeCompare(b.productName);
                                                                        }
                                                                    })
                                                                    .map((productStats) => {
                                                                    const high = productStats.high;
                                                                    const low = productStats.low;

                                                                    return (
                                                                        <div key={productStats.productId} className="p-4 box">
                                                                            <div className="text-base font-medium">
                                                                                {productStats.productName}
                                                                            </div>
                                                                            <div className="mt-3 space-y-3 text-xs">
                                                                                {/* Highest: icon, outlet group, price */}
                                                                                <div className="grid grid-cols-[18px_1fr_auto] items-center gap-2">
                                                                                    <Lucide
                                                                                        icon="ArrowUp"
                                                                                        className="w-4 h-4 text-success"
                                                                                    />
                                                                                    <div className="min-w-0 truncate text-slate-500">
                                                                                        {high
                                                                                            ? high.outletGroupName
                                                                                            : "-"}
                                                                                    </div>
                                                                                    <div className="tabular-nums font-medium">
                                                                                        {high
                                                                                            ? `RM ${high.price.toFixed(
                                                                                                  2
                                                                                              )}`
                                                                                            : "-"}
                                                                                    </div>
                                                                                </div>

                                                                                {/* Lowest: icon, outlet group, price */}
                                                                                <div className="grid grid-cols-[18px_1fr_auto] items-center gap-2">
                                                                                    <Lucide
                                                                                        icon="ArrowDown"
                                                                                        className="w-4 h-4 text-danger"
                                                                                    />
                                                                                    <div className="min-w-0 truncate text-slate-500">
                                                                                        {low
                                                                                            ? low.outletGroupName
                                                                                            : "-"}
                                                                                    </div>
                                                                                    <div className="tabular-nums font-medium">
                                                                                        {low
                                                                                            ? `RM ${low.price.toFixed(
                                                                                                  2
                                                                                              )}`
                                                                                            : "-"}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        </>
                                    ) : salesView === "Chain & Outlet" ? (
                                        <>
                                            <div className="col-span-12">
                                                <div className="p-5 box">
                                                    <div className="flex items-center gap-3">
                                                        <div className="text-base font-medium">
                                                            Sales Performance by Chain
                                                        </div>
                                                        <div className="ml-auto text-xs text-slate-500">
                                                            {companyId ? `Company: ${companyId}` : null}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mt-6">
                                                    {loadingOutletGroupSales ? (
                                                        <div className="flex items-center justify-center py-10">
                                                            <div className="text-center">
                                                                <Lucide
                                                                    icon="Loader2"
                                                                    className="w-7 h-7 animate-spin mx-auto mb-2"
                                                                />
                                                                <div>Loading chart data...</div>
                                                            </div>
                                                        </div>
                                                    ) : outletGroupSalesData.length === 0 ? (
                                                        <div className="text-center py-10 text-slate-500">
                                                            No data available for the selected filters.
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-12 gap-6">
                                                            {/* Hecto Litre Chart */}
                                                            <div className="col-span-12 lg:col-span-6">
                                                                <div className="mb-2 flex items-center justify-between">
                                                                    <div className="text-sm font-medium">Hecto Litre</div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setHectoLitreSortOrder(hectoLitreSortOrder === "asc" ? "desc" : "asc")}
                                                                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-700 transition-colors"
                                                                        title={`Sort ${hectoLitreSortOrder === "asc" ? "Descending" : "Ascending"}`}
                                                                    >
                                                                        <Lucide
                                                                            icon={hectoLitreSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                            className="w-4 h-4 text-blue-600"
                                                                        />
                                                                    </button>
                                                                </div>
                                                                <div className="h-full min-h-[320px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4">
                                                                    <Chart
                                                                        type="bar"
                                                                        width="auto"
                                                                        height={getBarChartHeightPx(sortedHectoLitreData.length)}
                                                                        data={hectoLitreChartData}
                                                                        options={hectoLitreChartOptions}
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Total Sales Chart */}
                                                            <div className="col-span-12 lg:col-span-6">
                                                                <div className="mb-2 flex items-center justify-between">
                                                                    <div className="text-sm font-medium">Total Sales (RM)</div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setSalesSortOrder(salesSortOrder === "asc" ? "desc" : "asc")}
                                                                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-700 transition-colors"
                                                                        title={`Sort ${salesSortOrder === "asc" ? "Descending" : "Ascending"}`}
                                                                    >
                                                                        <Lucide
                                                                            icon={salesSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                            className="w-4 h-4 text-blue-600"
                                                                        />
                                                                    </button>
                                                                </div>
                                                                <div className="h-full min-h-[320px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4">
                                                                    <Chart
                                                                        type="bar"
                                                                        width="auto"
                                                                        height={getBarChartHeightPx(sortedSalesData.length)}
                                                                        data={totalSalesChartData}
                                                                        options={totalSalesChartOptions}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    </div>
                                                    
                                                    {/* Table below bar charts */}
                                                    {outletGroupSalesData.length > 0 && (() => {
                                                        // Sort table data based on selected column
                                                        const sortedTableData = [...outletGroupSalesData].sort((a, b) => {
                                                            if (!tableSortColumn) {
                                                                return a.groupName.localeCompare(b.groupName);
                                                            }
                                                            
                                                            let comparison = 0;
                                                            switch (tableSortColumn) {
                                                                case "chain":
                                                                    comparison = a.groupName.localeCompare(b.groupName);
                                                                    break;
                                                                case "volume":
                                                                    comparison = a.totalHectoLitre - b.totalHectoLitre;
                                                                    break;
                                                                case "sales":
                                                                    comparison = a.totalSales - b.totalSales;
                                                                    break;
                                                                case "cartons":
                                                                    comparison = a.totalCartons - b.totalCartons;
                                                                    break;
                                                            }
                                                            
                                                            return tableSortOrder === "asc" ? comparison : -comparison;
                                                        });
                                                        
                                                        const handleSort = (column: "chain" | "volume" | "sales" | "cartons") => {
                                                            if (tableSortColumn === column) {
                                                                setTableSortOrder(tableSortOrder === "asc" ? "desc" : "asc");
                                                            } else {
                                                                setTableSortColumn(column);
                                                                setTableSortOrder("asc");
                                                            }
                                                        };
                                                        
                                                        // Calculate totals from all outlet group sales data
                                                        const totalVolume = outletGroupSalesData.reduce((sum, group) => sum + group.totalHectoLitre, 0);
                                                        const totalSales = outletGroupSalesData.reduce((sum, group) => sum + group.totalSales, 0);
                                                        const totalCartons = outletGroupSalesData.reduce((sum, group) => sum + group.totalCartons, 0);
                                                        
                                                        return (
                                                            <div className="mt-10">
                                                                <div className="overflow-x-auto">
                                                                    <Table className="border border-slate-200/60 dark:border-darkmode-400 text-sm">
                                                                        <Table.Thead>
                                                                            <Table.Tr>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("chain")}
                                                                                >
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        Chain
                                                                                        {tableSortColumn === "chain" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("volume")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Volume (hL)
                                                                                        {tableSortColumn === "volume" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("sales")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Sales (RM)
                                                                                        {tableSortColumn === "sales" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("cartons")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Cartons
                                                                                        {tableSortColumn === "cartons" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                            </Table.Tr>
                                                                        </Table.Thead>
                                                                        <Table.Tbody>
                                                                            {sortedTableData.map((group) => (
                                                                                <Table.Tr key={group.groupId}>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap">
                                                                                        {group.groupName}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap text-right">
                                                                                        {formatNumber(group.totalHectoLitre, {
                                                                                            minimumFractionDigits: 4,
                                                                                            maximumFractionDigits: 4,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap text-right">
                                                                                        RM {formatNumber(group.totalSales, {
                                                                                            minimumFractionDigits: 2,
                                                                                            maximumFractionDigits: 2,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap text-right">
                                                                                        {formatNumber(group.totalCartons, {
                                                                                            minimumFractionDigits: 0,
                                                                                            maximumFractionDigits: 0,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                </Table.Tr>
                                                                            ))}
                                                                        </Table.Tbody>
                                                                        <tfoot>
                                                                            <Table.Tr>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap">
                                                                                    Total
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    {formatNumber(totalVolume, {
                                                                                        minimumFractionDigits: 4,
                                                                                        maximumFractionDigits: 4,
                                                                                    })}
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    RM {formatNumber(totalSales, {
                                                                                        minimumFractionDigits: 2,
                                                                                        maximumFractionDigits: 2,
                                                                                    })}
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    {formatNumber(totalCartons, {
                                                                                        minimumFractionDigits: 0,
                                                                                        maximumFractionDigits: 0,
                                                                                    })}
                                                                                </Table.Td>
                                                                            </Table.Tr>
                                                                        </tfoot>
                                                                    </Table>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            
                                            {/* Second White Container */}
                                            <div className="col-span-12 mt-6 mb-4">
                                                <div className="p-5 box">
                                                    <div className="flex items-center gap-3">
                                                        <div className="text-base font-medium">
                                                            {selectedOutletName ? `${selectedOutletName} Sales Performance` : "Outlet Sales Performance"}
                                                        </div>
                                                        <div className="ml-auto flex items-center gap-3">
                                                            <div className="relative" ref={outletSearchRef}>
                                                                <div className="relative inline-flex">
                                                                    <Lucide
                                                                        icon="Search"
                                                                        className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                                    />
                                                                    <FormInput
                                                                        type="text"
                                                                        value={outletSearchQuery}
                                                                        onChange={(e) => {
                                                                            setOutletSearchQuery(e.target.value);
                                                                            setShowOutletDropdown(true);
                                                                        }}
                                                                        onFocus={() => {
                                                                            if (outletSearchQuery.trim()) {
                                                                                setShowOutletDropdown(true);
                                                                            }
                                                                        }}
                                                                        placeholder="Search outlet..."
                                                                        className="w-64 pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-200/60 dark:border-darkmode-400 text-sm"
                                                                    />
                                                                </div>
                                                                
                                                                {/* Dropdown with filtered results */}
                                                                {showOutletDropdown && filteredOutlets.length > 0 && (
                                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-darkmode-800 border border-slate-200/60 dark:border-darkmode-400 rounded-[0.6rem] shadow-lg z-50 max-h-60 overflow-y-auto">
                                                                        {filteredOutlets.map((outlet) => (
                                                                            <button
                                                                                key={outlet.id}
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSelectedOutlet(outlet.id);
                                                                                    setOutletSearchQuery(outlet.name);
                                                                                    setShowOutletDropdown(false);
                                                                                }}
                                                                                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-darkmode-700 transition-colors"
                                                                            >
                                                                                {outlet.name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* No results message */}
                                                                {showOutletDropdown && outletSearchQuery.trim() && filteredOutlets.length === 0 && !loadingOutlets && (
                                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-darkmode-800 border border-slate-200/60 dark:border-darkmode-400 rounded-[0.6rem] shadow-lg z-50 px-4 py-2 text-sm text-slate-500">
                                                                        No outlets found
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-slate-500">
                                                                {companyId ? `Company: ${companyId}` : null}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-6">
                                                        {!selectedOutlet ? (
                                                            <div className="text-center py-10 text-slate-500">
                                                                Please select an outlet to view sales performance.
                                                            </div>
                                                        ) : loadingSelectedOutletSales ? (
                                                            <div className="flex items-center justify-center py-10">
                                                                <div className="text-center">
                                                                    <Lucide
                                                                        icon="Loader2"
                                                                        className="w-7 h-7 animate-spin mx-auto mb-2"
                                                                    />
                                                                    <div>Loading outlet data...</div>
                                                                </div>
                                                            </div>
                                                        ) : selectedOutletSalesData.products.length === 0 ? (
                                                            <div className="text-center py-10 text-slate-500">
                                                                No product data available for the selected outlet.
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-12 gap-6">
                                                                {/* Products by Hecto Litre Chart - 40% width */}
                                                                <div className="col-span-12 lg:col-span-5">
                                                                    <div className="mb-2 text-sm font-medium">Products by Hecto Litre</div>
                                                                    <div className="h-full min-h-[320px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4">
                                                                        <Chart
                                                                            type="bar"
                                                                            width="auto"
                                                                            height={getBarChartHeightPx(selectedOutletSalesData.products.length)}
                                                                            data={outletProductsHectoLitreChartData}
                                                                            options={outletProductsHectoLitreChartOptions}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Products by Total Sales Chart - 40% width */}
                                                                <div className="col-span-12 lg:col-span-5">
                                                                    <div className="mb-2 text-sm font-medium">Products by Total Sales</div>
                                                                    <div className="h-full min-h-[320px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4">
                                                                        <Chart
                                                                            type="bar"
                                                                            width="auto"
                                                                            height={getBarChartHeightPx(selectedOutletSalesData.products.length)}
                                                                            data={outletProductsTotalSalesChartData}
                                                                            options={outletProductsTotalSalesChartOptions}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Summary Boxes - 20% width, aligned with Products by Total Sales */}
                                                                <div className="col-span-12 lg:col-span-2 flex flex-col gap-6">
                                                                    {/* Spacer to align with chart title */}
                                                                    <div className="h-[26px]"></div>
                                                                    
                                                                    {/* Hecto Litre Box */}
                                                                    <div className="p-4 box">
                                                                        <div className="flex items-center gap-2 text-base font-medium mb-3">
                                                                            <Lucide icon="Droplet" className="w-4 h-4" />
                                                                            Hecto Litre
                                                                        </div>
                                                                        <div className="tabular-nums font-medium text-sm">
                                                                            {formatNumber(selectedOutletSalesData.totalHectoLitre, {
                                                                                minimumFractionDigits: 4,
                                                                                maximumFractionDigits: 4,
                                                                            })} hL
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Total Sales Box */}
                                                                    <div className="p-4 box">
                                                                        <div className="flex items-center gap-2 text-base font-medium mb-3">
                                                                            <Lucide icon="CircleDollarSign" className="w-4 h-4" />
                                                                            Total Sales
                                                                        </div>
                                                                        <div className="tabular-nums font-medium text-sm">
                                                                            RM {formatNumber(selectedOutletSalesData.totalSales, {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Total Cartons Box */}
                                                                    <div className="p-4 box">
                                                                        <div className="flex items-center gap-2 text-base font-medium mb-3">
                                                                            <Lucide icon="Boxes" className="w-4 h-4" />
                                                                            Total (cartons)
                                                                        </div>
                                                                        <div className="tabular-nums font-medium text-sm">
                                                                            {formatNumber(selectedOutletSalesData.totalCartons, {
                                                                                minimumFractionDigits: 0,
                                                                                maximumFractionDigits: 0,
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : salesView === "SKU" ? (
                                        <>
                                            <div className="col-span-12">
                                                <div className="p-5 box">
                                                    <div className="flex items-center gap-3">
                                                        <div className="text-base font-medium">
                                                            Sales Performance by SKU
                                                        </div>
                                                        <div className="ml-auto text-xs text-slate-500">
                                                            {companyId ? `Company: ${companyId}` : null}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mt-6">
                                                    {loadingProductSales ? (
                                                        <div className="flex items-center justify-center py-10">
                                                            <div className="text-center">
                                                                <Lucide
                                                                    icon="Loader2"
                                                                    className="w-7 h-7 animate-spin mx-auto mb-2"
                                                                />
                                                                <div>Loading chart data...</div>
                                                            </div>
                                                        </div>
                                                    ) : productSalesData.length === 0 ? (
                                                        <div className="text-center py-10 text-slate-500">
                                                            No data available for the selected filters.
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-12 gap-6">
                                                            {/* Hecto Litre Chart */}
                                                            <div className="col-span-12 lg:col-span-6">
                                                                <div className="mb-2 flex items-center justify-between">
                                                                    <div className="text-sm font-medium">Hecto Litre</div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setHectoLitreSortOrder(hectoLitreSortOrder === "asc" ? "desc" : "asc")}
                                                                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-700 transition-colors"
                                                                        title={`Sort ${hectoLitreSortOrder === "asc" ? "Descending" : "Ascending"}`}
                                                                    >
                                                                        <Lucide
                                                                            icon={hectoLitreSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                            className="w-4 h-4 text-blue-600"
                                                                        />
                                                                    </button>
                                                                </div>
                                                                <div className="h-full min-h-[320px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4">
                                                                    <Chart
                                                                        type="bar"
                                                                        width="auto"
                                                                        height={getBarChartHeightPx(sortedHectoLitreData.length)}
                                                                        data={hectoLitreChartData}
                                                                        options={hectoLitreChartOptions}
                                                                    />
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Total Sales Chart */}
                                                            <div className="col-span-12 lg:col-span-6">
                                                                <div className="mb-2 flex items-center justify-between">
                                                                    <div className="text-sm font-medium">Total Sales (RM)</div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setSalesSortOrder(salesSortOrder === "asc" ? "desc" : "asc")}
                                                                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-700 transition-colors"
                                                                        title={`Sort ${salesSortOrder === "asc" ? "Descending" : "Ascending"}`}
                                                                    >
                                                                <Lucide
                                                                            icon={salesSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                            className="w-4 h-4 text-blue-600"
                                                                />
                                                                    </button>
                                                                </div>
                                                                <div className="h-full min-h-[320px] rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 p-4">
                                                                    <Chart
                                                                        type="bar"
                                                                        width="auto"
                                                                        height={getBarChartHeightPx(sortedSalesData.length)}
                                                                        data={totalSalesChartData}
                                                                        options={totalSalesChartOptions}
                                                                    />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    )}
                                                    </div>
                                                    
                                                    {/* Table below bar charts */}
                                                    {productSalesData.length > 0 && (() => {
                                                        // Sort table data based on selected column
                                                        const sortedTableData = [...productSalesData].sort((a, b) => {
                                                            if (!tableSortColumn) {
                                                                return a.productName.localeCompare(b.productName);
                                                            }
                                                            
                                                            let comparison = 0;
                                                            switch (tableSortColumn) {
                                                                case "sku":
                                                                    comparison = a.productName.localeCompare(b.productName);
                                                                    break;
                                                                case "volume":
                                                                    comparison = a.totalHectoLitre - b.totalHectoLitre;
                                                                    break;
                                                                case "sales":
                                                                    comparison = a.totalSales - b.totalSales;
                                                                    break;
                                                                case "cartons":
                                                                    comparison = a.totalCartons - b.totalCartons;
                                                                    break;
                                                            }
                                                            
                                                            return tableSortOrder === "asc" ? comparison : -comparison;
                                                        });
                                                        
                                                        const handleSort = (column: "sku" | "volume" | "sales" | "cartons") => {
                                                            if (tableSortColumn === column) {
                                                                setTableSortOrder(tableSortOrder === "asc" ? "desc" : "asc");
                                                            } else {
                                                                setTableSortColumn(column);
                                                                setTableSortOrder("asc");
                                                            }
                                                        };
                                                        
                                                        // Calculate totals from all product sales data
                                                        const totalVolume = productSalesData.reduce((sum, product) => sum + product.totalHectoLitre, 0);
                                                        const totalSales = productSalesData.reduce((sum, product) => sum + product.totalSales, 0);
                                                        const totalCartons = productSalesData.reduce((sum, product) => sum + product.totalCartons, 0);
                                                        
                                                        return (
                                                            <div className="mt-10">
                                                                <div className="overflow-x-auto">
                                                                    <Table className="border border-slate-200/60 dark:border-darkmode-400 text-sm">
                                                                        <Table.Thead>
                                                                            <Table.Tr>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("sku")}
                                                                                >
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        SKU
                                                                                        {tableSortColumn === "sku" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("volume")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Volume (hL)
                                                                                        {tableSortColumn === "volume" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("sales")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Sales (RM)
                                                                                        {tableSortColumn === "sales" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("cartons")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Cartons
                                                                                        {tableSortColumn === "cartons" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                            </Table.Tr>
                                                                        </Table.Thead>
                                                                        <Table.Tbody>
                                                                            {sortedTableData.map((product) => (
                                                                                <Table.Tr key={product.productId}>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap">
                                                                                        {product.productName}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap text-right">
                                                                                        {formatNumber(product.totalHectoLitre, {
                                                                                            minimumFractionDigits: 4,
                                                                                            maximumFractionDigits: 4,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap text-right">
                                                                                        RM {formatNumber(product.totalSales, {
                                                                                            minimumFractionDigits: 2,
                                                                                            maximumFractionDigits: 2,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 whitespace-nowrap text-right">
                                                                                        {formatNumber(product.totalCartons, {
                                                                                            minimumFractionDigits: 0,
                                                                                            maximumFractionDigits: 0,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                </Table.Tr>
                                                                            ))}
                                                                        </Table.Tbody>
                                                                        <tfoot>
                                                                            <Table.Tr>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap">
                                                                                    Total
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    {formatNumber(totalVolume, {
                                                                                        minimumFractionDigits: 4,
                                                                                        maximumFractionDigits: 4,
                                                                                    })}
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    RM {formatNumber(totalSales, {
                                                                                        minimumFractionDigits: 2,
                                                                                        maximumFractionDigits: 2,
                                                                                    })}
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    {formatNumber(totalCartons, {
                                                                                        minimumFractionDigits: 0,
                                                                                        maximumFractionDigits: 0,
                                                                                    })}
                                                                                </Table.Td>
                                                                            </Table.Tr>
                                                                        </tfoot>
                                                                    </Table>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            
                                            {/* Second White Container */}
                                            <div className="col-span-12 mt-6 mb-4">
                                                <div className="p-5 box">
                                                    <div className="flex items-center gap-3">
                                                        <div className="text-base font-medium">
                                                            {selectedProductName ? `${selectedProductName} Sales Performance` : "SKU Sales Performance"}
                                                        </div>
                                                        <div className="ml-auto flex items-center gap-3">
                                                            <div className="relative" ref={outletSearchRef}>
                                                                <div className="relative inline-flex">
                                                                    <Lucide
                                                                        icon="Search"
                                                                        className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                                    />
                                                                    <FormInput
                                                                        type="text"
                                                                        value={productSearchQuery}
                                                                        onChange={(e) => {
                                                                            setProductSearchQuery(e.target.value);
                                                                            setShowProductDropdown(true);
                                                                        }}
                                                                        onFocus={() => {
                                                                            if (productSearchQuery.trim()) {
                                                                                setShowProductDropdown(true);
                                                                            }
                                                                        }}
                                                                        placeholder="Search SKU..."
                                                                        className="w-64 pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-200/60 dark:border-darkmode-400 text-sm"
                                                                    />
                                                                </div>
                                                                
                                                                {/* Dropdown with filtered results */}
                                                                {showProductDropdown && filteredProducts.length > 0 && (
                                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-darkmode-800 border border-slate-200/60 dark:border-darkmode-400 rounded-[0.6rem] shadow-lg z-50 max-h-60 overflow-y-auto">
                                                                        {filteredProducts.map((product) => (
                                                                            <button
                                                                                key={product.id}
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSelectedProduct(product.id);
                                                                                    setProductSearchQuery(product.name);
                                                                                    setShowProductDropdown(false);
                                                                                }}
                                                                                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-darkmode-700 transition-colors"
                                                                            >
                                                                                {product.name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* No results message */}
                                                                {showProductDropdown && productSearchQuery.trim() && filteredProducts.length === 0 && !loadingProducts && (
                                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-darkmode-800 border border-slate-200/60 dark:border-darkmode-400 rounded-[0.6rem] shadow-lg z-50 px-4 py-2 text-sm text-slate-500">
                                                                        No products found
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-slate-500">
                                                                {companyId ? `Company: ${companyId}` : null}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-6">
                                                        {!selectedProduct ? (
                                                            <div className="text-center py-10 text-slate-500">
                                                                Please select a SKU to view sales performance.
                                                            </div>
                                                        ) : loadingProductChainSales ? (
                                                            <div className="flex items-center justify-center py-10">
                                                                <div className="text-center">
                                                                    <Lucide
                                                                        icon="Loader2"
                                                                        className="w-7 h-7 animate-spin mx-auto mb-2"
                                                                    />
                                                                    <div>Loading product data...</div>
                                                                </div>
                                                            </div>
                                                        ) : productChainSalesData.length === 0 ? (
                                                            <div className="text-center py-10 text-slate-500">
                                                                No chain data available for the selected SKU.
                                                            </div>
                                                        ) : (() => {
                                                            // Sort table data based on selected column
                                                            const sortedTableData = [...productChainSalesData].sort((a, b) => {
                                                                if (!tableSortColumn) {
                                                                    return a.groupName.localeCompare(b.groupName);
                                                                }
                                                                
                                                                let comparison = 0;
                                                                switch (tableSortColumn) {
                                                                    case "chain":
                                                                        comparison = a.groupName.localeCompare(b.groupName);
                                                                        break;
                                                                    case "volume":
                                                                        comparison = a.hectoLitre - b.hectoLitre;
                                                                        break;
                                                                    case "sales":
                                                                        comparison = a.totalSales - b.totalSales;
                                                                        break;
                                                                    case "cartons":
                                                                        comparison = a.totalCartons - b.totalCartons;
                                                                        break;
                                                                }
                                                                
                                                                return tableSortOrder === "asc" ? comparison : -comparison;
                                                            });
                                                            
                                                            const handleSort = (column: "chain" | "volume" | "sales" | "cartons") => {
                                                                if (tableSortColumn === column) {
                                                                    setTableSortOrder(tableSortOrder === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setTableSortColumn(column);
                                                                    setTableSortOrder("asc");
                                                                }
                                                            };
                                                            
                                                            // Calculate totals from all chain sales data
                                                            const totalVolume = productChainSalesData.reduce((sum, chain) => sum + chain.hectoLitre, 0);
                                                            const totalSales = productChainSalesData.reduce((sum, chain) => sum + chain.totalSales, 0);
                                                            const totalCartons = productChainSalesData.reduce((sum, chain) => sum + chain.totalCartons, 0);
                                                            
                                                            return (
                                                                <div className="overflow-x-auto">
                                                                    <Table className="border border-slate-200/60 dark:border-darkmode-400 text-sm w-full table-fixed">
                                                                        <Table.Thead>
                                                                            <Table.Tr>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    style={{ minWidth: '150px', maxWidth: '200px' }}
                                                                                    onClick={() => handleSort("chain")}
                                                                                >
                                                                                    <div className="flex items-center gap-1.5 truncate">
                                                                                        Chain
                                                                                        {tableSortColumn === "chain" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("volume")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Volume (hL)
                                                                                        {tableSortColumn === "volume" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("sales")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Sales (RM)
                                                                                        {tableSortColumn === "sales" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                                <Table.Th 
                                                                                    className="py-1.5 text-xs font-medium border-t border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-200 dark:bg-darkmode-600 text-slate-700 dark:text-slate-300 whitespace-nowrap text-right cursor-pointer hover:bg-slate-300 dark:hover:bg-darkmode-500 transition-colors"
                                                                                    onClick={() => handleSort("cartons")}
                                                                                >
                                                                                    <div className="flex items-center justify-end gap-1.5">
                                                                                        Total Cartons
                                                                                        {tableSortColumn === "cartons" ? (
                                                                                            <Lucide
                                                                                                icon={tableSortOrder === "asc" ? "ArrowUp" : "ArrowDown"}
                                                                                                className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300"
                                                                                            />
                                                                                        ) : (
                                                                                            <Lucide
                                                                                                icon="ArrowUpDown"
                                                                                                className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </Table.Th>
                                                                            </Table.Tr>
                                                                        </Table.Thead>
                                                                        <Table.Tbody>
                                                                            {sortedTableData.map((chain) => (
                                                                                <Table.Tr key={chain.groupId}>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 truncate" style={{ maxWidth: '200px' }}>
                                                                                        {chain.groupName}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 text-right" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                        {formatNumber(chain.hectoLitre, {
                                                                                            minimumFractionDigits: 4,
                                                                                            maximumFractionDigits: 4,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 text-right" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                        RM {formatNumber(chain.totalSales, {
                                                                                            minimumFractionDigits: 2,
                                                                                            maximumFractionDigits: 2,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                    <Table.Td className="py-1.5 text-xs border-b border-r border-slate-200/60 dark:border-darkmode-400 text-right" style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                        {formatNumber(chain.totalCartons, {
                                                                                            minimumFractionDigits: 0,
                                                                                            maximumFractionDigits: 0,
                                                                                        })}
                                                                                    </Table.Td>
                                                                                </Table.Tr>
                                                                            ))}
                                                                        </Table.Tbody>
                                                                        <tfoot>
                                                                            <Table.Tr>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-l border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap">
                                                                                    Total
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    {formatNumber(totalVolume, {
                                                                                        minimumFractionDigits: 4,
                                                                                        maximumFractionDigits: 4,
                                                                                    })}
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    RM {formatNumber(totalSales, {
                                                                                        minimumFractionDigits: 2,
                                                                                        maximumFractionDigits: 2,
                                                                                    })}
                                                                                </Table.Td>
                                                                                <Table.Td className="py-1.5 text-xs font-medium border-b border-r border-slate-200/60 dark:border-darkmode-400 bg-slate-100 dark:bg-darkmode-700 whitespace-nowrap text-right">
                                                                                    {formatNumber(totalCartons, {
                                                                                        minimumFractionDigits: 0,
                                                                                        maximumFractionDigits: 0,
                                                                                    })}
                                                                                </Table.Td>
                                                                            </Table.Tr>
                                                                        </tfoot>
                                                                    </Table>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : salesView === "Data Report" ? (
                                        <div className="col-span-12">
                                            <div className="mt-6 grid grid-cols-12 gap-x-6 gap-y-6">
                                                {loadingOutletGroups ? (
                                                    <div className="col-span-12 flex items-center justify-center py-10">
                                                        <div className="text-center">
                                                            <Lucide
                                                                icon="Loader2"
                                                                className="w-7 h-7 animate-spin mx-auto mb-2"
                                                            />
                                                            <div>Loading outlet groups...</div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    outletGroups.map((g) => (
                                                        <div key={g.id} className="col-span-12">
                                                            <div className="p-5 box">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="text-base font-medium">
                                                                        {g.groupName}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="ml-auto inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                                                        onClick={() => toggleOutletGroup(g.id)}
                                                                        aria-expanded={expandedOutletGroupIds.has(
                                                                            g.id
                                                                        )}
                                                                        aria-controls={`outletgroup-${g.id}`}
                                                                    >
                                                                        <Lucide
                                                                            icon={
                                                                                expandedOutletGroupIds.has(
                                                                                    g.id
                                                                                )
                                                                                    ? "ChevronUp"
                                                                                    : "ChevronDown"
                                                                            }
                                                                            className="w-5 h-5 stroke-[1.3]"
                                                                        />
                                                                    </button>
                                                                </div>

                                                                {expandedOutletGroupIds.has(g.id) && (
                                                                    <div
                                                                        id={`outletgroup-${g.id}`}
                                                                        className="mt-4"
                                                                    >
                                                                        {(() => {
                                                                            const isLoading =
                                                                                loadingOutletSales.has(g.id);
                                                                            const groupData = outletSalesData.get(g.id);
                                                                            const products = groupData?.products || [];
                                                                            const outlets = groupData?.outlets || [];

                                                                            if (!dateRangeISO) {
                                                                                return (
                                                                                    <div className="text-sm text-slate-500">
                                                                                        Please select a date range.
                                                                                    </div>
                                                                                );
                                                                            }

                                                                            if (isLoading) {
                                                                                return (
                                                                                    <div className="flex items-center justify-center py-10">
                                                                                        <div className="text-center">
                                                                                            <Lucide
                                                                                                icon="Loader2"
                                                                                                className="w-5 h-5 animate-spin mx-auto mb-2"
                                                                                            />
                                                                                            <div className="text-sm text-slate-500">
                                                                                                Loading outlet data...
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            }

                                                                            if (outlets.length === 0) {
                                                                                return (
                                                                                    <div className="text-sm text-slate-500">
                                                                                        No outlet data available for
                                                                                        this date range.
                                                                                    </div>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <div className="overflow-auto">
                                                                                    <Table className="border-b border-slate-200/60 text-xs" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                                                        <Table.Thead className="text-xs bg-white dark:bg-darkmode-600 sticky top-0 z-20">
                                                                                            <Table.Tr>
                                                                                                {/* Outlet column header */}
                                                                                                <Table.Td 
                                                                                                    className="py-1.5 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30"
                                                                                                    style={{ 
                                                                                                        minWidth: '200px', 
                                                                                                        width: '200px',
                                                                                                        boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)'
                                                                                                    }}
                                                                                                    rowSpan={2}
                                                                                                >
                                                                                                    Outlet
                                                                                                </Table.Td>
                                                                                                {/* Product column headers */}
                                                                                                {products.map((product, productIndex) => (
                                                                                                    <Table.Td
                                                                                                        key={product.id}
                                                                                                        className={`py-1.5 bg-green-100 dark:bg-green-900 relative text-center border-solid ${
                                                                                                            productIndex === 0 ? '' : 'border-l-2 border-l-slate-300 dark:border-l-slate-600'
                                                                                                        } border-r-2 border-r-slate-300 dark:border-r-slate-600`}
                                                                                                        colSpan={6}
                                                                                                        style={{
                                                                                                            minWidth: '490px',
                                                                                                            width: '490px',
                                                                                                            paddingRight: '4px',
                                                                                                            paddingLeft: '4px',
                                                                                                            borderLeftColor: productIndex === 0 ? undefined : '#cbd5e1',
                                                                                                            borderRightColor: '#cbd5e1',
                                                                                                            borderLeftStyle: productIndex === 0 ? undefined : 'solid',
                                                                                                            borderRightStyle: 'solid',
                                                                                                            borderLeftWidth: productIndex === 0 ? undefined : '2px',
                                                                                                            borderRightWidth: '2px'
                                                                                                        }}
                                                                                                    >
                                                                                                        <div className="font-medium whitespace-normal break-words leading-snug">{product.name}</div>
                                                                                                    </Table.Td>
                                                                                                ))}
                                                                                            </Table.Tr>
                                                                                            <Table.Tr>
                                                                                                {/* Sub-column headers for each product */}
                                                                                                {products.map((product, productIndex) => (
                                                                                                    <React.Fragment key={product.id}>
                                                                                                        <Table.Td
                                                                                                            className={`py-1.5 bg-green-100 dark:bg-green-900 relative border-l-2 border-solid text-center ${
                                                                                                                productIndex === 0 ? '' : 'border-l-slate-300 dark:border-l-slate-600'
                                                                                                            }`}
                                                                                                            style={{
                                                                                                                minWidth: '60px',
                                                                                                                width: '60px',
                                                                                                                borderLeftColor: productIndex === 0 ? undefined : '#cbd5e1',
                                                                                                                borderLeftStyle: 'solid',
                                                                                                                borderLeftWidth: productIndex === 0 ? undefined : '2px',
                                                                                                                borderRightStyle: 'dotted',
                                                                                                                borderRightWidth: '1px',
                                                                                                                borderRightColor: '#cbd5e1'
                                                                                                            }}
                                                                                                        >
                                                                                                            Opening
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid text-center"
                                                                                                            style={{
                                                                                                                minWidth: '60px',
                                                                                                                width: '60px',
                                                                                                                borderRightStyle: 'dotted',
                                                                                                                borderRightWidth: '1px',
                                                                                                                borderRightColor: '#cbd5e1'
                                                                                                            }}
                                                                                                        >
                                                                                                            Closing
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid text-center"
                                                                                                            style={{
                                                                                                                minWidth: '80px',
                                                                                                                width: '80px',
                                                                                                                borderRightStyle: 'dotted',
                                                                                                                borderRightWidth: '1px',
                                                                                                                borderRightColor: '#cbd5e1'
                                                                                                            }}
                                                                                                        >
                                                                                                            Sales (carton)
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid text-center"
                                                                                                            style={{
                                                                                                                minWidth: '90px',
                                                                                                                width: '90px',
                                                                                                                borderRightStyle: 'dotted',
                                                                                                                borderRightWidth: '1px',
                                                                                                                borderRightColor: '#cbd5e1'
                                                                                                            }}
                                                                                                        >
                                                                                                            Hecto Litre
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className="py-1.5 bg-green-100 dark:bg-green-900 relative border-solid text-center"
                                                                                                            style={{
                                                                                                                minWidth: '70px',
                                                                                                                width: '70px',
                                                                                                                borderRightStyle: 'dotted',
                                                                                                                borderRightWidth: '1px',
                                                                                                                borderRightColor: '#cbd5e1'
                                                                                                            }}
                                                                                                        >
                                                                                                            Price
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className="py-1.5 bg-green-100 dark:bg-green-900 border-r-2 border-r-slate-300 dark:border-r-slate-600 border-solid text-right"
                                                                                                            style={{
                                                                                                                minWidth: '120px',
                                                                                                                width: '120px',
                                                                                                                borderRightColor: '#cbd5e1',
                                                                                                                borderRightStyle: 'solid',
                                                                                                                borderRightWidth: '2px'
                                                                                                            }}
                                                                                                        >
                                                                                                            Sales (RM)
                                                                                                        </Table.Td>
                                                                                                    </React.Fragment>
                                                                                                ))}
                                                                                            </Table.Tr>
                                                                                        </Table.Thead>
                                                                                        <Table.Tbody>
                                                                                            {outlets.map((outlet) => (
                                                                                                <Table.Tr
                                                                                                    key={outlet.outletId}
                                                                                                    className="[&_td]:border-b [&_td]:border-slate-200/60"
                                                                                                >
                                                                                                    {/* Outlet name column (frozen) */}
                                                                                                    <Table.Td 
                                                                                                        className="py-1.5 bg-white dark:bg-darkmode-600 text-xs sticky left-0 z-20"
                                                                                                        style={{
                                                                                                            minWidth: '200px',
                                                                                                            width: '200px',
                                                                                                            borderRightStyle: 'dotted',
                                                                                                            borderRightWidth: '1px',
                                                                                                            borderRightColor: '#94a3b8',
                                                                                                            boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)'
                                                                                                        }}
                                                                                                    >
                                                                                                        {outlet.outletName}
                                                                                                    </Table.Td>
                                                                                                    {/* Product columns */}
                                                                                                    {products.map((product, productIndex) => {
                                                                                                        const productData = outlet.products[product.id];
                                                                                                        return (
                                                                                                            <React.Fragment key={product.id}>
                                                                                                                <Table.Td
                                                                                                                    className={`py-1.5 dark:bg-darkmode-600 text-xs border-l-2 border-solid text-center ${
                                                                                                                        productIndex === 0 ? '' : 'border-l-slate-300 dark:border-l-slate-600'
                                                                                                                    }`}
                                                                                                                    style={{
                                                                                                                        minWidth: '60px',
                                                                                                                        width: '60px',
                                                                                                                        borderLeftColor: productIndex === 0 ? undefined : '#cbd5e1',
                                                                                                                        borderLeftStyle: 'solid',
                                                                                                                        borderLeftWidth: productIndex === 0 ? undefined : '2px',
                                                                                                                        borderRightStyle: 'dotted',
                                                                                                                        borderRightWidth: '1px',
                                                                                                                        borderRightColor: '#cbd5e1'
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {productData?.opening !== null && productData?.opening !== undefined 
                                                                                                                        ? Math.round(productData.opening).toString() 
                                                                                                                        : '-'}
                                                                                                                </Table.Td>
                                                                                                                <Table.Td
                                                                                                                    className="py-1.5 dark:bg-darkmode-600 text-xs border-solid text-center"
                                                                                                                    style={{
                                                                                                                        minWidth: '60px',
                                                                                                                        width: '60px',
                                                                                                                        borderRightStyle: 'dotted',
                                                                                                                        borderRightWidth: '1px',
                                                                                                                        borderRightColor: '#cbd5e1'
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {productData?.closing !== null && productData?.closing !== undefined 
                                                                                                                        ? Math.round(productData.closing).toString() 
                                                                                                                        : '-'}
                                                                                                                </Table.Td>
                                                                                                                <Table.Td
                                                                                                                    className="py-1.5 dark:bg-darkmode-600 text-xs border-solid text-center"
                                                                                                                    style={{
                                                                                                                        minWidth: '80px',
                                                                                                                        width: '80px',
                                                                                                                        borderRightStyle: 'dotted',
                                                                                                                        borderRightWidth: '1px',
                                                                                                                        borderRightColor: '#cbd5e1'
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {productData?.qty !== null && productData?.qty !== undefined 
                                                                                                                        ? Math.round(productData.qty).toString() 
                                                                                                                        : '-'}
                                                                                                                </Table.Td>
                                                                                                                <Table.Td
                                                                                                                    className="py-1.5 dark:bg-darkmode-600 text-xs border-solid text-right"
                                                                                                                    style={{
                                                                                                                        minWidth: '90px',
                                                                                                                        width: '90px',
                                                                                                                        borderRightStyle: 'dotted',
                                                                                                                        borderRightWidth: '1px',
                                                                                                                        borderRightColor: '#cbd5e1'
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {productData?.hectoLitre !== null && productData?.hectoLitre !== undefined
                                                                                                                        ? productData.hectoLitre.toFixed(4)
                                                                                                                        : productData?.qty !== null && productData?.qty !== undefined && productData?.grossWeightPerUOM !== null && productData?.grossWeightPerUOM !== undefined
                                                                                                                        ? (productData.qty * productData.grossWeightPerUOM).toFixed(4)
                                                                                                                        : '-'}
                                                                                                                </Table.Td>
                                                                                                                <Table.Td
                                                                                                                    className="py-1.5 dark:bg-darkmode-600 text-xs border-solid text-right"
                                                                                                                    style={{
                                                                                                                        minWidth: '70px',
                                                                                                                        width: '70px',
                                                                                                                        borderRightStyle: 'dotted',
                                                                                                                        borderRightWidth: '1px',
                                                                                                                        borderRightColor: '#cbd5e1'
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {productData?.price !== null && productData?.price !== undefined 
                                                                                                                        ? productData.price.toFixed(2) 
                                                                                                                        : '-'}
                                                                                                                </Table.Td>
                                                                                                                <Table.Td
                                                                                                                    className="py-1.5 dark:bg-darkmode-600 text-xs border-r-2 border-r-slate-300 dark:border-r-slate-600 border-solid"
                                                                                                                    style={{
                                                                                                                        minWidth: '120px',
                                                                                                                        width: '120px',
                                                                                                                        maxWidth: '120px',
                                                                                                                        borderRightColor: '#cbd5e1',
                                                                                                                        borderRightStyle: 'solid',
                                                                                                                        borderRightWidth: '2px',
                                                                                                                        textAlign: 'right',
                                                                                                                        overflow: 'hidden',
                                                                                                                        whiteSpace: 'nowrap',
                                                                                                                        paddingRight: '8px'
                                                                                                                    }}
                                                                                                                >
                                                                                                                    <div style={{ textAlign: 'right', width: '100%', display: 'block' }}>
                                                                                                                        {productData?.total !== null && productData?.total !== undefined
                                                                                                                            ? formatNumber(productData.total, {
                                                                                                                                minimumFractionDigits: 2,
                                                                                                                                maximumFractionDigits: 2
                                                                                                                            })
                                                                                                                            : '-'}
                                                                                                                    </div>
                                                                                                                </Table.Td>
                                                                                                            </React.Fragment>
                                                                                                        );
                                                                                                    })}
                                                                                                </Table.Tr>
                                                                                            ))}
                                                                                        </Table.Tbody>
                                                                                    </Table>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="col-span-12">
                                            <div className="p-5 box">
                                                <div className="flex items-center gap-3">
                                                    <div className="text-base font-medium">
                                                        Sales: Chain & Outlet
                                                    </div>
                                                    <div className="ml-auto text-xs text-slate-500">
                                                        {companyId ? `Company: ${companyId}` : null}
                                                    </div>
                                                </div>

                                                <div className="mt-4 text-sm text-slate-500">
                                                    Analysis Report will be added here (trends, contribution, SKU
                                                    mix, etc.). For now this is a placeholder view switch.
                                                </div>

                                                <div className="mt-4 grid grid-cols-12 gap-4">
                                                    <div className="col-span-12 lg:col-span-6">
                                                        <div className="p-5 box">
                                                            <div className="flex items-center gap-2">
                                                                <Lucide
                                                                    icon="LineChart"
                                                                    className="w-4 h-4"
                                                                />
                                                                <div className="text-sm font-medium">
                                                                    Trend (Placeholder)
                                                                </div>
                                                            </div>
                                                            <div className="mt-2 text-sm text-slate-500">-</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-span-12 lg:col-span-6">
                                                        <div className="p-5 box">
                                                            <div className="flex items-center gap-2">
                                                                <Lucide
                                                                    icon="BarChart3"
                                                                    className="w-4 h-4"
                                                                />
                                                                <div className="text-sm font-medium">
                                                                    Breakdown (Placeholder)
                                                                </div>
                                                            </div>
                                                            <div className="mt-2 text-sm text-slate-500">-</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}


